<template>
  <div class="orders-page">
    <header class="op-head">
      <div>
        <h1 class="op-title">Торговые ордера</h1>
        <div class="op-sub">{{ headerSub }}</div>
      </div>
      <div class="op-stats">
        <div class="op-stat">
          <div class="op-stat-lbl">В очереди</div>
          <div class="op-stat-val">{{ queue.length }}</div>
        </div>
        <div class="op-stat">
          <div class="op-stat-lbl">Истекает в ближайшие 5 мин</div>
          <div class="op-stat-val">{{ expiringCount }}</div>
        </div>
        <div class="op-stat">
          <div class="op-stat-lbl">Сумма заблокировано</div>
          <div class="op-stat-val">{{ lockedSumText }}</div>
        </div>
      </div>
    </header>

    <!-- Filter bar: статусы мульти-выбор + направление. Меняет фильтр →
         сразу refetch. Дефолтный preset «Активные» совпадает с тем что
         было раньше — back-compat для оператора, открывающего страницу. -->
    <div class="op-filters">
      <div class="op-filter-group">
        <span class="op-filter-label">Статус</span>
        <button
          v-for="opt in statusFilterOptions"
          :key="opt.key"
          type="button"
          class="op-chip"
          :class="{ 'op-chip-active': isStatusActive(opt.key) }"
          @click="toggleStatus(opt.key)"
        >{{ opt.label }}</button>
      </div>
      <div class="op-filter-group">
        <span class="op-filter-label">Тип</span>
        <button
          v-for="opt in ['ALL','BUY','SELL']"
          :key="opt"
          type="button"
          class="op-chip"
          :class="{ 'op-chip-active': (filters.exType || 'ALL') === opt }"
          @click="setExType(opt)"
        >{{ opt }}</button>
      </div>
    </div>

    <section class="op-card">
      <div class="op-card-head">
        <span>Найдено: {{ queue.length }}</span>
        <button type="button" class="op-btn-ghost" @click="loadQueue" :disabled="queueLoading">
          {{ queueLoading ? 'Обновляю…' : '↻ Обновить' }}
        </button>
      </div>

      <!-- Horizontal scroll wrapper: широкая таблица (10 колонок с
           длинными адресами / kpi) не всегда влезает в 1280–1440px.
           Заворачиваем в overflow-x — на маленьком экране будет
           горизонтальный скролл; таблица min-width'ом держит верстку. -->
      <div class="op-table-wrap">
      <table class="op-table">
        <thead>
          <tr>
            <th style="width:60px">#</th>
            <th>Клиент</th>
            <th>Тип</th>
            <th>Списано</th>
            <th>К получению</th>
            <th>Курс</th>
            <th>Комиссия</th>
            <th>Статус</th>
            <th>Истекает</th>
            <th style="width:200px">Действия</th>
          </tr>
        </thead>
        <tbody v-if="queue.length">
          <tr v-for="order in queue" :key="order.orderId" :class="rowClass(order)">
            <td>#{{ order.orderId }}</td>
            <td>{{ order.userLabel || ('user ' + order.userId) }}</td>
            <td>
              <span class="op-extype" :class="'op-extype-' + order.exType.toLowerCase()">{{ order.exType }}</span>
            </td>
            <td class="op-amount">
              {{ formatAmount(order.sellAmount, order.sellAsset) }}
              <span v-if="order.sellNetwork" class="op-net">{{ order.sellNetwork }}</span>
            </td>
            <td class="op-amount">
              {{ formatAmount(order.buyAmount, order.buyAsset) }}
              <span v-if="order.buyNetwork" class="op-net">{{ order.buyNetwork }}</span>
            </td>
            <td class="op-rate">{{ formatRate(order.lockedPrice) }}</td>
            <td class="op-fee">{{ formatFee(order) }}</td>
            <td>
              <span class="op-status" :class="statusCss(order.effectiveStatus || order.status)">
                {{ statusLabel(order.effectiveStatus || order.status) }}
              </span>
            </td>
            <td class="op-when" :class="{
                  'op-when-urgent':
                    (order.status === 'PENDING' && remainingMs(order) < 5*60*1000 && remainingMs(order) > 0) ||
                    ((order.effectiveStatus || order.status) === 'AWAITING_RECONFIRM' && reconfirmRemainingMs(order) > 0)
                }">
              <template v-if="order.status === 'PENDING' && remainingMs(order) > 0">{{ formatRemaining(remainingMs(order)) }}</template>
              <template v-else-if="order.status === 'PENDING'">истёк</template>
              <template v-else-if="(order.effectiveStatus || order.status) === 'AWAITING_RECONFIRM' && reconfirmRemainingMs(order) > 0" title="Осталось у клиента на ответ по новому курсу">{{ formatRemaining(reconfirmRemainingMs(order)) }}</template>
              <template v-else-if="(order.effectiveStatus || order.status) === 'AWAITING_RECONFIRM'">истёк</template>
              <template v-else>—</template>
            </td>
            <td>
              <div class="op-row-actions">
                <button
                  v-if="(order.effectiveStatus || order.status) === 'PENDING'"
                  type="button"
                  class="op-btn-ok"
                  :disabled="actionBusy[order.orderId]"
                  @click="startExecution(order)"
                  title="Дёрнуть свежую цену, проверить дрифт ±0.3%"
                >Начать</button>
                <button
                  v-if="canCancel(order)"
                  type="button"
                  class="op-btn-no"
                  :disabled="actionBusy[order.orderId]"
                  @click="cancelOrder(order)"
                  :title="(order.effectiveStatus || order.status) === 'EXECUTING' ? 'Refund клиенту (USD уже списан, крипта куплена)' : 'Возврат резерва клиенту'"
                >Отменить</button>
                <span
                  v-if="(order.effectiveStatus || order.status) === 'AWAITING_RECONFIRM'"
                  class="op-reconfirm-wait"
                  :title="order.reconfirmRate ? ('Клиенту предложен новый курс ' + formatRate(order.reconfirmRate) + ' USD') : 'Клиенту отправлено на ре-подтверждение'"
                >
                  <span class="op-pending-label">ждём клиента</span>
                  <span v-if="order.reconfirmRate" class="op-reconfirm-rate">новый курс {{ formatRate(order.reconfirmRate) }} USD</span>
                  <span
                    v-if="reconfirmRemainingMs(order) > 0"
                    class="op-reconfirm-left"
                  >осталось {{ formatRemaining(reconfirmRemainingMs(order)) }}</span>
                  <span v-else-if="order.reconfirmExpiresAt" class="op-reconfirm-left op-reconfirm-expired">срок истёк</span>
                </span>
                <!-- Settlement actions for EXECUTING orders. Next-step
                     button подсвечивается на основе settlement-status,
                     который дотягивается отдельным GET'ом при загрузке
                     очереди. -->
                <template v-if="(order.effectiveStatus || order.status) === 'EXECUTING'">
                  <!-- «Запросить re-confirm» — доступен пока settlement
                       не создан (обе стороны). Для BUY это до record-fill,
                       для SELL — до банковского перевода. -->
                  <button
                    v-if="!order.settlement"
                    type="button"
                    class="op-btn-ghost"
                    :disabled="actionBusy[order.orderId]"
                    @click="openReconfirmModal(order)"
                    title="Отправить ордер клиенту на подтверждение нового курса"
                  >↩ Reconfirm</button>
                  <!-- Шаг 1 (BUY и SELL): fill — источник, цена, объём, комиссия. -->
                  <button
                    v-if="!order.settlement"
                    type="button"
                    class="op-btn-ok"
                    :disabled="actionBusy[order.orderId]"
                    @click="openFillModal(order)"
                    :title="order.exType === 'BUY' ? 'Купить крипту (биржа/резервы) и записать fill' : 'Продать крипту клиента (биржа/резервы) и записать fill'"
                  >Записать fill</button>
                  <!-- Шаг 2 BUY: отправка крипты клиенту on-chain. -->
                  <button
                    v-else-if="order.exType === 'BUY' && order.settlement.status === 'PENDING'"
                    type="button"
                    class="op-btn-info"
                    :disabled="actionBusy[order.orderId]"
                    @click="openBroadcastModal(order)"
                    title="Отправить крипту on-chain и записать tx_hash"
                  >Broadcast</button>
                  <!-- Шаг 2 SELL: банковский перевод USD клиенту. -->
                  <button
                    v-else-if="order.exType === 'SELL' && order.settlement.status === 'PENDING'"
                    type="button"
                    class="op-btn-info"
                    :disabled="actionBusy[order.orderId]"
                    @click="openBankTransferModal(order)"
                    title="Записать банковский перевод USD клиенту → COMPLETED"
                  >Банк-перевод</button>
                  <button
                    v-else-if="order.settlement.status === 'BROADCAST'"
                    type="button"
                    class="op-btn-ok"
                    :disabled="actionBusy[order.orderId]"
                    @click="confirmSettlement(order)"
                    title="Подтвердить N сетевых подтверждений → COMPLETED"
                  >Confirm</button>
                  <span
                    v-else
                    class="op-pending-label op-pending-info"
                  >исполняется</span>
                </template>
                <button type="button" class="op-btn-link" @click="openHistory(order)">История</button>
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr><td colspan="10" class="op-empty">Ордеров в очереди нет</td></tr>
        </tbody>
      </table>
      </div>
    </section>

    <!-- Record-fill modal: трейдер вписывает данные исполнения с LP -->
    <div v-if="fillOrder" class="op-modal-overlay" @click.self="closeFillModal">
      <div class="op-modal">
        <div class="op-modal-head">
          Записать fill — ордер #{{ fillOrder.orderId }} ({{ fillOrder.exType }})
          <button type="button" class="op-modal-close" @click="closeFillModal">×</button>
        </div>
        <div class="op-modal-body">
          <!-- Источник крипты: покупка на бирже (LP) или выдача из
               собственных резервов компании. -->
          <div class="op-form-row">
            <label>Источник исполнения</label>
            <div class="op-source-toggle">
              <button
                type="button"
                class="op-source-opt"
                :class="{ active: fillForm.fillSource === 'EXCHANGE' }"
                @click="fillForm.fillSource = 'EXCHANGE'"
              >Биржа (LP)</button>
              <button
                type="button"
                class="op-source-opt"
                :class="{ active: fillForm.fillSource === 'INTERNAL' }"
                @click="fillForm.fillSource = 'INTERNAL'"
              >Собственные резервы</button>
            </div>
          </div>

          <div v-if="!fillIsInternal" class="op-form-row">
            <label>Exchange Order ID *</label>
            <input
              type="text"
              v-model="fillForm.exchangeOrderId"
              :class="{ 'op-input-invalid': !String(fillForm.exchangeOrderId || '').trim() }"
              placeholder="Например: 9281234.0 (LP order id)"
            />
          </div>
          <!-- Напоминание что было обещано клиенту в момент создания
               ордера — трейдер видит референс перед вводом реальных
               данных с биржи. -->
          <div class="op-fill-promise">
            <div class="op-fill-promise-row">
              <span>Клиенту обещано:</span>
              <b>{{ formatAmount(fillOrder.buyAmount, fillOrder.buyAsset) }}</b>
            </div>
            <div class="op-fill-promise-row">
              <span>Locked price:</span>
              <b>{{ formatRate(fillOrder.lockedPrice) }} USD / 1 {{ cryptoTicker(fillOrder) }}</b>
            </div>
          </div>

          <div class="op-form-grid">
            <div class="op-form-row">
              <label>Fill price (USD за 1 {{ cryptoTicker(fillOrder) }}) *</label>
              <input
                type="number" step="any"
                v-model.number="fillForm.fillPrice"
                :class="{ 'op-input-invalid': !(Number(fillForm.fillPrice) > 0) }"
                placeholder="100050.00"
              />
              <div class="op-form-sub">Дефолт = locked price. Измените если LP исполнил по другой цене.</div>
            </div>
            <div class="op-form-row">
              <label>Fill qty ({{ cryptoTicker(fillOrder) }}) *</label>
              <input
                type="number" step="any"
                v-model.number="fillForm.fillQty"
                :class="{ 'op-input-invalid': !(Number(fillForm.fillQty) > 0) }"
                placeholder="0.0001"
              />
            </div>
          </div>
          <div class="op-form-row">
            <label>{{ fillIsInternal ? 'Комиссия, USD *' : 'LP commission, USD *' }}</label>
            <input
              type="number" step="any"
              v-model.number="fillForm.exchangeFeeUsd"
              :class="{ 'op-input-invalid': fillForm.exchangeFeeUsd === null || fillForm.exchangeFeeUsd === '' || Number(fillForm.exchangeFeeUsd) < 0 }"
              placeholder="0.10"
            />
            <div class="op-form-sub">
              {{ fillIsInternal
                ? 'Из собственных резервов комиссии биржи нет — обычно 0.'
                : 'Комиссия партнёрской биржи (Binance ~0.10%). Если нулевая — введите 0.' }}
            </div>
          </div>
          <!-- Автопредложение re-confirm: реальный fill price ушёл от
               locked price в убыточную для компании сторону (зависит
               от типа ордера). Предлагаем вернуть сделку клиенту на
               подтверждение нового курса вместо записи fill'а. -->
          <div v-if="fillDeviationIsLoss" class="op-fill-deviation">
            <div class="op-fill-deviation-head">
              ⚠ Fill price отклонился на
              <b>{{ fillDeviationPct > 0 ? '+' : '' }}{{ fillDeviationPct.toFixed(2) }}%</b>
              {{ fillOrder.exType === 'SELL' ? '— нужно переподтверждение' : '— компания в минусе' }}
            </div>
            <div class="op-fill-deviation-body">
              Locked: {{ formatRate(fillOrder.lockedPrice) }} · Fill: {{ formatRate(fillForm.fillPrice) }}.
              Отклонение превысило лимит — запись fill заблокирована.
              <b>Переподтверждение курса у клиента обязательно.</b>
            </div>
            <button type="button" class="op-btn-info op-fill-deviation-cta" @click="reconfirmFromFill">
              ↩ Запросить re-confirm (обязательно)
            </button>
          </div>

          <!-- Отклонение в пользу компании — просто информируем,
               re-confirm не нужен. -->
          <div v-else-if="fillDeviationIsGain" class="op-fill-deviation op-fill-deviation-gain">
            <div class="op-fill-deviation-head">
              ✓ Fill price отклонился на
              <b>{{ fillDeviationPct > 0 ? '+' : '' }}{{ fillDeviationPct.toFixed(2) }}%</b>
              — в пользу компании
            </div>
            <div class="op-fill-deviation-body">
              Locked: {{ formatRate(fillOrder.lockedPrice) }} · Fill: {{ formatRate(fillForm.fillPrice) }}.
              Разница остаётся у компании, клиент получает обещанное. Можно записывать fill.
            </div>
          </div>

          <!-- Спред в пределах лимита — просто информируем (loss/gain-блоки
               выше перекрывают случай выхода за лимит). -->
          <div v-else-if="Number(fillForm.fillPrice) > 0 && Number(fillOrder.lockedPrice) > 0"
               class="op-fill-deviation op-fill-deviation-info">
            <div class="op-fill-deviation-head">
              Спред к курсу клиента:
              <b>{{ fillDeviationPct > 0 ? '+' : '' }}{{ fillDeviationPct.toFixed(2) }}%</b>
            </div>
            <div class="op-fill-deviation-body">
              Locked: {{ formatRate(fillOrder.lockedPrice) }} · Fill: {{ formatRate(fillForm.fillPrice) }}. В пределах лимита.
            </div>
          </div>

          <div class="op-form-hint">
            После записи fill создаётся settlement-строка в PENDING,
            дальше идёт Broadcast — отправка крипты клиенту on-chain.
          </div>
        </div>
        <div class="op-modal-foot">
          <button class="op-btn-ghost" @click="closeFillModal">Отмена</button>
          <button
            class="op-btn-ok"
            :disabled="fillBusy || !fillReady || fillDeviationIsLoss"
            :title="fillDeviationIsLoss ? 'Курс отклонился за лимит — сначала обязателен re-confirm' : ''"
            @click="submitFill"
          >
            {{ fillBusy ? 'Сохраняю…' : 'Записать' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Broadcast modal: BUY-only -->
    <div v-if="broadcastOrder" class="op-modal-overlay" @click.self="closeBroadcastModal">
      <div class="op-modal">
        <div class="op-modal-head">
          Broadcast — ордер #{{ broadcastOrder.orderId }} (BUY)
          <button type="button" class="op-modal-close" @click="closeBroadcastModal">×</button>
        </div>
        <div class="op-modal-body">
          <div class="op-form-row">
            <label>tx_hash транзакции в сети</label>
            <input type="text" v-model.trim="broadcastForm.txHash" placeholder="0x1234abcd…" />
          </div>
          <div class="op-form-row">
            <label>Фактический network fee, USD</label>
            <input type="number" step="any" v-model.number="broadcastForm.actualNetworkFeeUsd" placeholder="2.50" />
          </div>

          <div class="op-form-hint">
            KYW-проверка адреса выполнена на шаге Start Execution.
            Settlement перейдёт в BROADCAST. После N подтверждений в сети
            нажмите Confirm — это закроет ордер в COMPLETED.
          </div>
        </div>
        <div class="op-modal-foot">
          <button class="op-btn-ghost" @click="closeBroadcastModal">Отмена</button>
          <button class="op-btn-ok" :disabled="broadcastBusy || !broadcastReady" @click="submitBroadcast">
            {{ broadcastBusy ? 'Сохраняю…' : 'Broadcast' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bank transfer modal: SELL-only. Бэкофис вручную перевёл USD на
         банковский счёт клиента и записывает референс перевода. -->
    <div v-if="bankTransferOrder" class="op-modal-overlay" @click.self="closeBankTransferModal">
      <div class="op-modal">
        <div class="op-modal-head">
          Банковский перевод — ордер #{{ bankTransferOrder.orderId }} (SELL)
          <button type="button" class="op-modal-close" @click="closeBankTransferModal">×</button>
        </div>
        <div class="op-modal-body">
          <div class="op-bank-card">
            <div class="op-bank-row">
              <span class="op-bank-key">Получатель</span>
              <span class="op-bank-val">{{ bankTransferOrder.destinationWalletName || '—' }}</span>
            </div>
            <div class="op-bank-row">
              <span class="op-bank-key">Счёт (IBAN)</span>
              <span class="op-bank-val op-addr-mono">{{ bankTransferOrder.destinationBankAccount || '—' }}</span>
            </div>
            <div class="op-bank-row">
              <span class="op-bank-key">БИК / SWIFT</span>
              <span class="op-bank-val op-addr-mono">{{ bankTransferOrder.destinationBankBic || '—' }}</span>
            </div>
            <div class="op-bank-row">
              <span class="op-bank-key">Сумма к переводу</span>
              <span class="op-bank-val">{{ bankTransferOrder.buyAmount }} USD</span>
            </div>
          </div>

          <div class="op-form-row">
            <label>Референс перевода (SWIFT / MT103 / № платёжного поручения) *</label>
            <input type="text" v-model.trim="bankTransferForm.bankReference" placeholder="напр. MT103-2026-0042" />
          </div>

          <div class="op-form-row">
            <label>Платёжка / подтверждение перевода *</label>
            <input type="file" ref="bankReceiptInput"
                   accept=".pdf,.png,.jpg,.jpeg,.webp"
                   @change="onBankReceiptChange" />
            <div v-if="bankTransferFile" class="op-file-picked">
              📎 {{ bankTransferFile.name }}
              <button type="button" class="op-file-clear" @click="clearBankReceipt">убрать</button>
            </div>
          </div>
          <div class="op-form-hint">
            Крипта клиента уже списана на этапе fill. После сохранения
            перевода ордер закроется в COMPLETED.
          </div>
        </div>
        <div class="op-modal-foot">
          <button class="op-btn-ghost" @click="closeBankTransferModal">Отмена</button>
          <button
            class="op-btn-ok"
            :disabled="bankTransferBusy || !bankTransferReady"
            @click="submitBankTransfer"
          >
            {{ bankTransferBusy ? 'Сохраняю…' : 'Записать перевод' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Start Execution KYW gate: перед тем как трейдер тратит деньги
         на LP, compliance должен проверить кошелёк получателя. FLAGGED
         адрес — оператор закрывает модалку и жмёт Cancel из очереди,
         так как ордер ещё в PENDING (release reserve без потерь). -->
    <div v-if="startTarget" class="op-modal-overlay" @click.self="closeStartModal">
      <div class="op-modal">
        <div class="op-modal-head">
          Start Execution — ордер #{{ startTarget.orderId }} ({{ startTarget.exType }})
          <button type="button" class="op-modal-close" @click="closeStartModal">×</button>
        </div>
        <div class="op-modal-body">
          <div class="op-fill-promise">
            <div class="op-fill-promise-row">
              <span>Клиент получит:</span>
              <b>{{ formatAmount(startTarget.buyAmount, startTarget.buyAsset) }}</b>
            </div>
            <div class="op-fill-promise-row">
              <span>Адрес получателя:</span>
              <b class="op-addr-mono">{{ (startTarget.destinationWalletAddress || '—') }}</b>
            </div>
            <div class="op-fill-promise-row">
              <span>Сеть:</span>
              <b>{{ startTarget.buyNetwork || '—' }}</b>
            </div>
          </div>

          <!-- Авто KYT: дёргаем AMLBot по адресу получателя. Вердикт
               определяет, можно ли стартовать. -->
          <div class="op-kyt">
            <div class="op-kyt-head">
              <button
                type="button"
                class="op-btn-ghost"
                :disabled="kytBusy"
                @click="runKytCheck"
              >
                {{ kytBusy ? 'Проверяю…' : (kytResult ? 'Проверить заново' : 'Проверить адрес') }}
              </button>
              <span
                v-if="kytResult"
                class="op-kyt-badge"
                :class="'op-kyt-' + kytResult.verdict.toLowerCase()"
              >
                {{ kytVerdictLabel(kytResult.verdict) }}
                <template v-if="kytResult.riskscore != null">
                  · risk {{ (kytResult.riskscore * 100).toFixed(0) }}/100
                </template>
              </span>
            </div>
            <div v-if="kytError" class="op-kyt-error">{{ kytError }}</div>
            <!-- На основе чего вынесен FLAGGED. -->
            <div v-if="kytResult && kytResult.flagReason" class="op-kyt-reason op-kyt-flagreason">
              <div class="op-kyt-reason-title">На основе чего заблокировано:</div>
              <div class="op-kyt-msg">{{ kytResult.flagReason }}</div>
            </div>
            <!-- Причина не-CLEAN вердикта: сообщение + сработавшие категории. -->
            <div v-if="kytResult && kytResult.verdict !== 'CLEAN'" class="op-kyt-reason">
              <div v-if="kytResult.message" class="op-kyt-msg">{{ kytResult.message }}</div>
              <template v-if="kytResult.signals && kytResult.signals.length">
                <div class="op-kyt-reason-title">Сработавшие категории:</div>
                <div class="op-kyt-sig-list">
                  <span
                    v-for="sig in kytResult.signals"
                    :key="sig.category"
                    class="op-kyt-sig"
                    :class="{ 'op-kyt-sig-risky': sig.risky }"
                  >{{ signalLabel(sig.category) }} · {{ (sig.weight * 100).toFixed(0) }}%</span>
                </div>
              </template>
              <div v-else-if="kytResult.verdict === 'FLAGGED'" class="op-kyt-msg">
                Детализация категорий недоступна (тестовый режим AMLBot).
              </div>
            </div>
            <!-- Детали AMLBot для бэкофиса. -->
            <div v-if="kytResult" class="op-kyt-details">
              <div v-if="kytResult.devMode" class="op-kyt-dev">⚠ Тестовый режим AMLBot — данные не боевые</div>
              <div v-if="kytResult.counterpartyAddress" class="op-kyt-drow">
                <span>Контрагент:</span> <span class="op-addr-mono">{{ kytResult.counterpartyAddress }}</span>
              </div>
              <template v-if="kytResult.connections && kytResult.connections.length">
                <button type="button" class="op-kyt-toggle" @click="toggleAmlDetails">
                  <span class="op-kyt-toggle-chev">{{ amlDetailsVisible ? '▾' : '▸' }}</span>
                  {{ amlDetailsVisible ? 'Свернуть' : 'Расширить (' + kytResult.connections.length + ')' }}
                </button>
                <div v-show="amlDetailsVisible">
                  <div
                    v-for="(c, i) in kytResult.connections"
                    :key="'conn-' + i"
                    class="op-kyt-conn"
                    :class="{ 'op-kyt-conn-risky': c.riskscore >= 0.5 }"
                  >
                    <span class="op-kyt-conn-name">{{ c.name }}</span>
                    <span class="op-kyt-conn-type">{{ connTypeLabel(c.type) }}</span>
                    <span class="op-kyt-conn-risk">риск {{ (c.riskscore * 100).toFixed(0) }}/100</span>
                    <span class="op-kyt-conn-meta">{{ c.hops }} переходов</span>
                  </div>
                </div>
              </template>
              <div v-if="kytResult.uid" class="op-kyt-drow"><span>UID:</span> <span class="op-addr-mono">{{ kytResult.uid }}</span></div>
            </div>
            <a
              v-if="kytResult && kytResult.pdfReport && kytResult.pdfReport !== '#'"
              :href="kytResult.pdfReport" target="_blank" rel="noopener" class="op-kyt-link"
            >Полный отчёт AMLBot ↗</a>
            <a
              class="op-kyt-link"
              href="https://amlbot.com/"
              target="_blank"
              rel="noopener"
            >AMLBot ↗</a>
          </div>

          <!-- Двойное ревью MLRO + Compliance при не-CLEAN KYT. -->
          <div v-if="needsReview" class="op-kyt-review">
            <div class="op-kyt-reason-title">Решение MLRO + Compliance</div>
            <div class="op-kyt-rev-grid">
              <div class="op-kyt-rev-card" :class="reviewCardClass(kytReviewState && kytReviewState.mlro)">
                <div class="op-kyt-rev-role">MLRO</div>
                <div class="op-kyt-rev-dec">{{ reviewDecisionLabel(kytReviewState && kytReviewState.mlro) }}</div>
              </div>
              <div class="op-kyt-rev-card" :class="reviewCardClass(kytReviewState && kytReviewState.compliance)">
                <div class="op-kyt-rev-role">Compliance</div>
                <div class="op-kyt-rev-dec">{{ reviewDecisionLabel(kytReviewState && kytReviewState.compliance) }}</div>
              </div>
            </div>
            <div v-for="(rv, i) in (kytReviewState && kytReviewState.reviews) || []" :key="'rv-' + i" class="op-kyt-rev-comment">
              <b>{{ rv.reviewerRole === 'MLRO' ? 'MLRO' : 'Compliance' }}:</b> «{{ rv.comment }}»
            </div>

            <template v-if="myReviewRoles.length">
              <div v-for="rrole in myReviewRoles" :key="rrole" class="op-kyt-rev-action">
                <div class="op-kyt-rev-action-head">
                  Решение как {{ roleLabel(rrole) }}
                  <span v-if="roleDecision(rrole)">— сейчас: {{ reviewDecisionLabel(roleDecision(rrole)) }} (можно пересмотреть)</span>
                </div>
                <textarea v-model="reviewComments[rrole]" rows="2" placeholder="Обоснование решения для audit"></textarea>
                <div class="op-kyt-rev-actions">
                  <button class="op-btn-ok" :disabled="reviewBusy || !(reviewComments[rrole] || '').trim()" @click="submitReview('ACCEPT', rrole)">✓ Принять</button>
                  <button class="op-btn-no" :disabled="reviewBusy || !(reviewComments[rrole] || '').trim()" @click="submitReview('REJECT', rrole)">✕ Отклонить</button>
                </div>
              </div>
            </template>
            <div v-else class="op-kyt-note">Решение принимают роли MLRO и Compliance.</div>
          </div>

          <div v-if="kytResult" class="op-form-row">
            <label>Комментарий (для аудита)</label>
            <textarea
              v-model="startForm.kywNote"
              rows="2"
              placeholder="Например: AMLBot CLEAN, риск низкий"
            ></textarea>
          </div>

          <div class="op-form-hint">
            Если KYT не CLEAN — старт возможен только когда <b>оба</b> ревьюера
            (MLRO и Compliance) приняли. Любой отказ блокирует исполнение —
            используйте <b>Отменить</b> в строке очереди. Резерв клиента
            вернётся без потерь, т.к. деньги на LP ещё не потрачены.
          </div>
        </div>
        <div class="op-modal-foot">
          <button class="op-btn-ghost" @click="closeStartModal">Отмена</button>
          <button class="op-btn-ok" :disabled="!canStart" @click="submitStart">
            {{ startBusy ? 'Начинаю…' : 'Start Execution' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Request-Reconfirm modal: трейдер вручную отправляет
         EXECUTING-ордер обратно на подтверждение клиенту с fresh mid.
         Причина обязательна для audit-log. -->
    <div v-if="reconfirmTarget" class="op-modal-overlay" @click.self="closeReconfirmModal">
      <div class="op-modal">
        <div class="op-modal-head">
          Запросить re-confirm — ордер #{{ reconfirmTarget.orderId }} ({{ reconfirmTarget.exType }})
          <button type="button" class="op-modal-close" @click="closeReconfirmModal">×</button>
        </div>
        <div class="op-modal-body">
          <div class="op-fill-promise">
            <div class="op-fill-promise-row">
              <span>Locked price:</span>
              <b>{{ formatRate(reconfirmTarget.lockedPrice) }} USD / 1 {{ cryptoTicker(reconfirmTarget) }}</b>
            </div>
            <div class="op-fill-promise-row">
              <span>Клиенту обещано:</span>
              <b>{{ formatAmount(reconfirmTarget.buyAmount, reconfirmTarget.buyAsset) }}</b>
            </div>
          </div>

          <div class="op-form-row">
            <label>Актуальный курс, USD за 1 {{ cryptoTicker(reconfirmTarget) }} *</label>
            <input
              type="number" step="any"
              v-model.number="reconfirmForm.rate"
              :class="{ 'op-input-invalid': reconfirmForm.rate === null || reconfirmForm.rate === '' || Number(reconfirmForm.rate) <= 0 }"
              placeholder="напр. 1845.20"
            />
            <div class="op-form-sub" v-if="reconfirmRateDriftPct !== null">
              Отклонение от locked: <b>{{ reconfirmRateDriftPct > 0 ? '+' : '' }}{{ reconfirmRateDriftPct.toFixed(2) }}%</b>
            </div>
          </div>

          <div class="op-form-row">
            <label>Причина (обязательно) *</label>
            <textarea
              v-model="reconfirmForm.note"
              @input="onReconfirmNoteInput"
              rows="3"
              placeholder="Заполнится автоматически из курса — можно отредактировать"
            ></textarea>
          </div>

          <div class="op-form-hint">
            Ордер вернётся в <b>AWAITING_RECONFIRM</b>. Клиенту будет
            предложен указанный курс, у него будет 5 минут на подтверждение.
            Если откажется — Cancel через reject-путь с возвратом резерва.
            Резерв на балансе остаётся locked всё это время.
          </div>
        </div>
        <div class="op-modal-foot">
          <button class="op-btn-ghost" @click="closeReconfirmModal">Отмена</button>
          <button class="op-btn-info" :disabled="reconfirmBusy || !reconfirmReady" @click="submitReconfirm">
            {{ reconfirmBusy ? 'Отправляю…' : 'Запросить' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Cancel modal: операторская отмена ордера. Обязательный
         причинный код, опциональный комментарий (обязательный для
         OTHER). Для EXECUTING-кейса в шапке висит дополнительный
         warning про refund. -->
    <div v-if="cancelTarget" class="op-modal-overlay" @click.self="closeCancelModal">
      <div class="op-modal">
        <div class="op-modal-head">
          Отменить ордер #{{ cancelTarget.orderId }} ({{ cancelTarget.exType }})
          <button type="button" class="op-modal-close" @click="closeCancelModal">×</button>
        </div>
        <div class="op-modal-body">
          <!-- Warning только для EXECUTING — там USD уже consumed -->
          <div v-if="cancelIsRefund" class="op-cancel-warn">
            <b>Refund-сценарий.</b> USD клиента уже списан и крипта куплена на бирже.
            Возврат сделает CreditBalance(USD) на счёт клиента; крипта останется
            на счёте компании как inventory. Settlement → FAILED.
          </div>

          <div class="op-form-row">
            <label>Причина отмены *</label>
            <select v-model="cancelForm.reason">
              <option value="" disabled>Выберите причину</option>
              <option v-for="r in cancelReasons" :key="r.code" :value="r.code">{{ r.label }}</option>
            </select>
          </div>

          <div class="op-form-row">
            <label>Комментарий <span v-if="cancelForm.reason === 'OTHER'">*</span></label>
            <textarea
              v-model="cancelForm.comment"
              :placeholder="cancelForm.reason === 'KYW_FLAGGED' ? 'Например: связан с Tornado Cash, источник Chainalysis' : 'Свободный комментарий для audit-trail'"
              rows="3"
            ></textarea>
          </div>

          <div class="op-form-hint">
            Запишется как событие <code>CANCELLED_BY_ADMIN</code> в журнал ордера.
          </div>
        </div>
        <div class="op-modal-foot">
          <button class="op-btn-ghost" @click="closeCancelModal">Закрыть</button>
          <button class="op-btn-no" :disabled="cancelBusy || !cancelReady" @click="submitCancel">
            {{ cancelBusy ? 'Отменяю…' : (cancelIsRefund ? 'Refund + Cancel' : 'Отменить заявку') }}
          </button>
        </div>
      </div>
    </div>

    <!-- History modal -->
    <div v-if="historyOrder" class="op-modal-overlay" @click.self="closeHistory">
      <div class="op-modal">
        <div class="op-modal-head">
          История ордера #{{ historyOrder.orderId }}
          <button type="button" class="op-modal-close" @click="closeHistory">×</button>
        </div>
        <div class="op-modal-body">
          <div v-if="historyLoading">Загружаю…</div>
          <div v-else-if="!historyEvents.length">Событий пока нет</div>
          <div v-else class="op-history-list">
            <div v-for="event in historyEvents" :key="event.eventId" class="op-history-item">
              <div class="op-history-dot" :class="historyDotClass(event)">{{ historyIcon(event) }}</div>
              <div class="op-history-body">
                <div class="op-history-row">
                  <div class="op-history-label">{{ historyLabel(event) }}</div>
                  <div class="op-history-time">{{ formatDateTimeFull(event.occurredAt) }}</div>
                </div>
                <div class="op-history-actor">
                  <template v-if="event.actorType === 'CLIENT'">Клиент</template>
                  <template v-else-if="event.actorType === 'OPERATOR'">{{ event.actorName || event.actorEmail || 'оператор' }}</template>
                  <template v-else>Система</template>
                </div>
                <div v-if="event.note" class="op-history-note">«{{ event.note }}»</div>
                <!-- Типизированный рендер payload'а: набор label/value
                     строк, локализованных и форматированных под тип
                     события. Сырой JSON — только за toggle'ом «детали»,
                     для случаев когда оператор хочет скопировать payload. -->
                <div v-if="payloadRows(event).length" class="op-history-rows">
                  <div v-for="(row, idx) in payloadRows(event)" :key="idx" class="op-history-row-pair">
                    <div class="op-history-pair-k">{{ row.label }}</div>
                    <div class="op-history-pair-v" :class="row.cssClass">
                      <template v-if="row.kind === 'rate-pair'">
                        <span class="op-rate-old">{{ row.oldValue }}</span>
                        <span class="op-rate-arrow">→</span>
                        <span class="op-rate-new">{{ row.newValue }}</span>
                      </template>
                      <template v-else>{{ row.value }}</template>
                    </div>
                  </div>
                </div>
                <button
                  v-if="event.eventType === 'SETTLEMENT_BANK_TRANSFER' && event.payload && event.payload.hasReceipt"
                  type="button"
                  class="op-btn-toggle"
                  @click="downloadBankReceipt(historyOrder.orderId)"
                >📎 Платёжка</button>
                <button
                  v-if="event.payload && Object.keys(event.payload).length"
                  type="button"
                  class="op-btn-toggle"
                  @click="toggleRawPayload(event.eventId)"
                >{{ rawPayloadOpen[event.eventId] ? '−' : '+' }} JSON</button>
                <pre v-if="rawPayloadOpen[event.eventId]" class="op-history-payload">{{ JSON.stringify(event.payload, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain, getHeader } from '../../config/config'

export default {
  name: 'OrdersQueue',
  data() {
    return {
      queue: [],
      queueLoading: false,
      expireBusy: false,
      now: Date.now(),
      tickerHandle: null,
      pollHandle: null,
      // per-row busy флаги — чтобы один клик не дёргал endpoint два раза.
      actionBusy: {},

      historyOrder: null,
      historyEvents: [],
      historyLoading: false,
      rawPayloadOpen: {}, // eventId → bool (показывать JSON-блок)

      // Settlement modals state
      fillOrder: null,
      fillForm: this.emptyFillForm(),
      fillBusy: false,
      // Порог отклонения fill price от locked price (%), при котором
      // модалка предлагает re-confirm вместо записи fill. = backend
      // RATE_TOLERANCE_BPS / 100.
      fillDeviationWarnPct: 0.3,
      broadcastOrder: null,
      broadcastForm: this.emptyBroadcastForm(),
      broadcastBusy: false,

      // Bank-transfer modal state (SELL settlement)
      bankTransferOrder: null,
      bankTransferForm: { bankReference: '' },
      bankTransferFile: null,
      bankTransferBusy: false,

      // Start-execution modal state (авто KYT-gate для BUY)
      startTarget: null,
      startForm: { kywNote: '' },
      kytResult: null,   // { verdict, riskscore, asset, address, status }
      kytBusy: false,
      kytError: '',
      kytReviewState: null,   // { reviews, mlro, compliance, bothAccepted, anyRejected }
      reviewComments: { MLRO: '', COMPILANCE: '' },
      reviewBusy: false,
      // Список контрагентов AMLBot сворачиваем (модалка длинная). null —
      // авто: CLEAN раскрыт, не-CLEAN свёрнут.
      amlDetailsOpen: null,

      // Request-Reconfirm modal (EXECUTING → AWAITING_RECONFIRM
      // вручную). Trigger'ится трейдером когда fresh курс на бирже
      // ушёл сильно после Start Execution.
      reconfirmTarget: null,
      reconfirmForm: { note: '', rate: null },
      // Причина авто-генерируется из курса, пока оператор её не
      // отредактировал вручную — тогда флаг гасим и не перетираем.
      reconfirmNoteAuto: true,
      reconfirmBusy: false,
      startBusy: false,

      // Cancel modal state
      cancelTarget: null,
      cancelForm: this.emptyCancelForm(),
      cancelBusy: false,
      cancelReasons: [
        { code: 'KYW_FLAGGED',           label: 'KYW флаг (адрес связан с риском)' },
        { code: 'CLIENT_REQUEST',        label: 'По запросу клиента' },
        { code: 'TECHNICAL_ERROR',       label: 'Техническая ошибка' },
        { code: 'COMPLIANCE_HOLD',       label: 'Compliance-задержка / расследование' },
        { code: 'INSUFFICIENT_LIQUIDITY',label: 'Нет ликвидности на бирже' },
        { code: 'DUPLICATE_ORDER',       label: 'Дубликат / ошибочный заказ' },
        { code: 'OTHER',                 label: 'Другое (укажите в комментарии)' },
      ],

      // Filters. statuses=[] означает «активные» (бэк дефолтит сам);
      // selectedStatusPreset храним для подсветки чипов.
      filters: {
        statuses: [], // [] = active default, ['ALL'] = всё, иначе явно
        exType: '',   // '' = ALL
      },
      statusFilterOptions: [
        { key: 'ACTIVE',             label: 'Активные' },
        { key: 'PENDING',            label: 'Pending' },
        { key: 'AWAITING_RECONFIRM', label: 'Reconfirm' },
        { key: 'EXECUTING',          label: 'Executing' },
        { key: 'COMPLETED',          label: 'Completed' },
        { key: 'EXPIRED',            label: 'Expired' },
        { key: 'CANCELLED',          label: 'Отменённые (клиент)' },
        { key: 'REJECTED',           label: 'Отклонённые (бэкофис)' },
        { key: 'ALL',                label: 'Все' },
      ],
    }
  },
  computed: {
    fillIsInternal() {
      return this.fillForm.fillSource === 'INTERNAL'
    },
    fillReady() {
      const f = this.fillForm
      // exchange_order_id обязателен только для биржи; для INTERNAL сервер
      // сам присвоит internal_{orderId}. fill price/qty — суть исполнения,
      // комиссия LP введена явно (0 допустим, но не пустым полем).
      const orderIdOk = this.fillIsInternal || !!(String(f.exchangeOrderId || '').trim())
      return orderIdOk &&
        Number(f.fillPrice) > 0 &&
        Number(f.fillQty) > 0 &&
        f.exchangeFeeUsd !== null && f.exchangeFeeUsd !== '' && Number(f.exchangeFeeUsd) >= 0
    },
    // Отклонение реального fill price от locked price (что обещали
    // клиенту). Signed %: положительное = крипта подорожала (компания
    // в минусе на BUY), отрицательное = подешевела.
    fillDeviationPct() {
      if (!this.fillOrder) return 0
      const locked = Number(this.fillOrder.lockedPrice) || 0
      const fill = Number(this.fillForm.fillPrice) || 0
      if (locked <= 0 || fill <= 0) return 0
      return ((fill - locked) / locked) * 100
    },
    // Направление убытка компании зависит от типа ордера:
    //   BUY  — убыток когда fill ВЫШЕ locked (купили крипту дороже,
    //          чем продали клиенту по locked rate) → deviation > 0.
    //   SELL — убыток когда fill НИЖЕ locked (продали крипту клиента
    //          дешевле, чем начислили ему USD) → deviation < 0.
    // Только в убыточную сторону есть смысл предлагать re-confirm —
    // иначе компания молча ест разницу. В прибыльную сторону просто
    // информируем, без CTA.
    fillDeviationIsLoss() {
      if (!this.fillOrder) return false
      const dev = this.fillDeviationPct
      if (Math.abs(dev) <= this.fillDeviationWarnPct) return false
      // BUY — убыток когда fill выше locked (купили дороже).
      if (this.fillOrder.exType === 'BUY') return dev > 0
      // SELL на бирже — убыток только когда fill ниже locked (продали
      // крипту клиента дешевле). Личные резервы (INTERNAL) — двусторонний
      // гейт: внешней сделки нет, разницу несём на своей книге.
      if (this.fillForm.fillSource === 'INTERNAL') return true
      return dev < 0
    },
    fillDeviationIsGain() {
      if (!this.fillOrder) return false
      const dev = this.fillDeviationPct
      if (Math.abs(dev) <= this.fillDeviationWarnPct) return false
      // Информационный «плюс без блокировки»: BUY — fill ниже locked;
      // SELL на бирже — fill выше locked. Для INTERNAL плюса нет — гейт.
      if (this.fillOrder.exType === 'BUY') return dev < 0
      return this.fillForm.fillSource !== 'INTERNAL' && dev > 0
    },
    broadcastReady() {
      const b = this.broadcastForm
      // KYW-проверка уже сделана на шаге Start Execution — здесь
      // только валидируем что оператор ввёл tx_hash.
      return !!(b.txHash && b.txHash.length >= 10)
    },
    bankTransferReady() {
      // Шаг 2 SELL: только референс перевода + обязательная платёжка.
      // Источник/цена/объём зафиксированы на fill.
      const reference = String(this.bankTransferForm.bankReference || '').trim()
      return reference.length >= 3 && !!this.bankTransferFile
    },
    reconfirmReady() {
      return !!(String(this.reconfirmForm.note || '').trim()) &&
        Number(this.reconfirmForm.rate) > 0
    },
    // Не-CLEAN KYT требует двойного ревью (MLRO + Compliance).
    needsReview() {
      return !!this.kytResult && this.kytResult.verdict !== 'CLEAN'
    },
    // Роли, под которыми текущий пользователь может ставить решение.
    // ADMIN действует как обе роли по отдельности; выделенный ревьюер —
    // только под своей ролью.
    myReviewRoles() {
      let lu = {}
      try { lu = JSON.parse(window.localStorage.getItem('loginedUser') || '{}') } catch (e) { lu = {} }
      const roles = ((lu && lu.userRoles) || []).map(r => String((r && r.name) || '').toUpperCase())
      if (roles.indexOf('ADMIN') !== -1) return ['MLRO', 'COMPILANCE']
      const result = []
      if (roles.indexOf('MLRO') !== -1) result.push('MLRO')
      if (roles.indexOf('COMPILANCE') !== -1) result.push('COMPILANCE')
      return result
    },
    reviewBothAccepted() { return !!(this.kytReviewState && this.kytReviewState.bothAccepted) },
    reviewAnyRejected() { return !!(this.kytReviewState && this.kytReviewState.anyRejected) },
    // Видимость списка контрагентов: явный выбор оператора, иначе авто —
    // CLEAN раскрыт, не-CLEAN свёрнут (там список обычно длинный).
    amlDetailsVisible() {
      if (this.amlDetailsOpen !== null) return this.amlDetailsOpen
      return !this.kytResult || this.kytResult.verdict === 'CLEAN'
    },
    // Старт разрешён, когда KYT выполнен: CLEAN — сразу, иначе только
    // когда оба ревьюера приняли. Backend дублирует эту проверку.
    canStart() {
      if (this.startBusy || this.kytBusy) return false
      if (!this.kytResult) return false
      if (this.needsReview && !this.reviewBothAccepted) return false
      return true
    },
    // Отклонение введённого курса от locked price ордера (для подсказки
    // оператору). null пока курс не введён или нет locked.
    reconfirmRateDriftPct() {
      if (!this.reconfirmTarget) return null
      const locked = Number(this.reconfirmTarget.lockedPrice) || 0
      const rate = Number(this.reconfirmForm.rate) || 0
      if (!locked || !rate) return null
      return ((rate - locked) / locked) * 100
    },
    cancelIsRefund() {
      // Refund (а не обычный release) только когда fill уже записан —
      // т.е. есть settlement. EXECUTING без settlement = деньги ещё
      // не потрачены на LP, это обычный release резерва.
      if (!this.cancelTarget) return false
      const status = this.cancelTarget.effectiveStatus || this.cancelTarget.status
      return status === 'EXECUTING' && !!this.cancelTarget.settlement
    },
    cancelReady() {
      const f = this.cancelForm
      if (!f.reason) return false
      // Для OTHER комментарий обязателен — иначе непонятно что за
      // «другое», audit-trail бесполезен.
      if (f.reason === 'OTHER' && !f.comment.trim()) return false
      return true
    },
    headerSub() {
      if (this.filters.statuses.includes('ALL')) return 'Все ордера за всё время — newest first'
      if (this.filters.statuses.length === 0) return 'Активные ордера: PENDING / AWAITING_RECONFIRM / EXECUTING'
      return 'Фильтр: ' + this.filters.statuses.join(', ')
    },
    expiringCount() {
      // PENDING ордера, истекающие в течение 5 минут — оператору
      // полезно видеть число, чтобы перераспределить нагрузку трейдеров.
      // effectiveStatus учитывает lazy-expiry (order уже истёк по TTL, но
      // в БД status ещё PENDING) — таких не считаем.
      return this.queue.filter((order) => {
        const ms = this.remainingMs(order)
        const status = order.effectiveStatus || order.status
        return ms > 0 && ms < 5 * 60 * 1000 && status === 'PENDING'
      }).length
    },
    lockedSumText() {
      // Сумма sell_amount по USD-стороне только для ордеров, где резерв
      // фактически залочен:
      //   PENDING / AWAITING_RECONFIRM — 100% залочено
      //   EXECUTING без settlement — залочено (fill ещё не записан)
      //   EXECUTING с settlement — уже consumed, не считаем
      // COMPLETED / EXPIRED / CANCELLED / REJECTED — не считаем
      // (резерв уже возвращён или потрачен). Используем effectiveStatus
      // чтобы lazy-expired PENDING'и не завышали цифру.
      const isLocked = (order) => {
        const status = order.effectiveStatus || order.status
        if (status === 'PENDING' || status === 'AWAITING_RECONFIRM') return true
        if (status === 'EXECUTING') {
          // settlement появляется при RecordFill — если он есть, USD
          // уже потрачен на LP-покупку, не залочен больше.
          return !order.settlement
        }
        return false
      }
      const sum = this.queue
        .filter((order) => order.sellAsset === 'USD')
        .filter(isLocked)
        .reduce((acc, order) => acc + Number(order.sellAmount || 0), 0)
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(sum) + ' USD'
    },
  },
  watch: {
    // Пока причину не трогали руками — держим её синхронной с курсом.
    'reconfirmForm.rate'() {
      if (this.reconfirmNoteAuto) {
        this.reconfirmForm.note = this.buildReconfirmNote(this.reconfirmForm.rate)
      }
    },
  },
  mounted() {
    this.loadQueue()
    // Тикер для countdown'а — раз в секунду; API не дёргает.
    this.tickerHandle = setInterval(() => { this.now = Date.now() }, 1000)
    // Polling раз в 30 сек — чтобы оператор видел новые ордера без
    // ручного «обновить».
    this.pollHandle = setInterval(() => { this.loadQueue() }, 30 * 1000)
  },
  beforeDestroy() {
    if (this.tickerHandle) clearInterval(this.tickerHandle)
    if (this.pollHandle) clearInterval(this.pollHandle)
  },
  methods: {
    loadQueue() {
      this.queueLoading = true
      const params = {}
      if (this.filters.statuses.length) params.status = this.filters.statuses.join(',')
      if (this.filters.exType) params.exType = this.filters.exType
      axios.get(`${apiDomain}/admin/orders/queue`, { params, headers: getHeader() })
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : []
          this.queue = list
          list.forEach((order) => {
            this.hydrateUserLabel(order)
            if ((order.effectiveStatus || order.status) === 'EXECUTING') {
              this.hydrateSettlement(order)
            }
          })
        })
        .catch((err) => {
          console.error('orders queue load failed:', err)
        })
        .finally(() => { this.queueLoading = false })
    },
    isStatusActive(key) {
      if (key === 'ACTIVE') return this.filters.statuses.length === 0
      if (key === 'ALL') return this.filters.statuses.length === 1 && this.filters.statuses[0] === 'ALL'
      return this.filters.statuses.includes(key)
    },
    toggleStatus(key) {
      // Single-select: каждый клик заменяет выбор, не добавляет.
      // Клик по уже активному — no-op, чтобы случайный двойной клик
      // не отправлял пустой запрос.
      if (this.isStatusActive(key)) return
      if (key === 'ACTIVE') {
        // «Активные» — пустой список (бэк сам подставит набор
        // PENDING/AWAITING/EXECUTING).
        this.filters.statuses = []
      } else if (key === 'ALL') {
        this.filters.statuses = ['ALL']
      } else {
        this.filters.statuses = [key]
      }
      this.loadQueue()
    },
    setExType(value) {
      this.filters.exType = value === 'ALL' ? '' : value
      this.loadQueue()
    },
    hydrateSettlement(order) {
      axios.get(`${apiDomain}/admin/orders/${order.orderId}/settlement`, { headers: getHeader() })
        .then((res) => {
          // 204 → undefined; иначе settlement-объект.
          this.$set(order, 'settlement', res.status === 204 ? null : (res.data || null))
        })
        .catch(() => { this.$set(order, 'settlement', null) })
    },
    cryptoTicker(order) {
      return order.exType === 'BUY' ? order.buyAsset : order.sellAsset
    },
    emptyFillForm() {
      return { fillSource: 'EXCHANGE', exchangeOrderId: '', fillPrice: null, fillQty: null, exchangeFeeUsd: 0 }
    },
    emptyBroadcastForm() {
      return { txHash: '', actualNetworkFeeUsd: 0 }
    },
    openFillModal(order) {
      this.fillOrder = order
      this.fillForm = this.emptyFillForm()
      // Пред-заполняем fillPrice = locked_price (что было обещано).
      // fillQty — чистое количество к исполнению на бирже: BUY —
      // buy_amount; SELL — база без нашей комиссии в крипте
      // (sell_amount уже включает company_fee_crypto сверху).
      this.fillForm.fillPrice = order.lockedPrice
      this.fillForm.fillQty = order.exType === 'BUY'
        ? order.buyAmount
        : order.sellAmount - (order.companyFeeCrypto || 0)
    },
    closeFillModal() {
      this.fillOrder = null
      this.fillForm = this.emptyFillForm()
    },
    // toast — единая обёртка над bvToast с дефолтами Payton-стиля.
    // variant: 'info' | 'success' | 'warning' | 'danger'
    toast(message, title, variant) {
      if (!this.$bvToast) return
      this.$bvToast.toast(message, {
        title: title || 'Уведомление',
        variant: variant || 'info',
        toaster: 'b-toaster-top-center',
        solid: true,
        autoHideDelay: 4000,
      })
    },
    submitFill() {
      if (!this.fillReady) return
      // Жёсткий гейт: при отклонении за лимит запись fill запрещена —
      // обязателен re-confirm. Дублирует серверную проверку.
      if (this.fillDeviationIsLoss) {
        this.toast('Курс отклонился за лимит — сначала проведите re-confirm.', 'Требуется re-confirm', 'warning')
        return
      }
      this.fillBusy = true
      const orderID = this.fillOrder.orderId
      this.$set(this.actionBusy, orderID, true)
      axios.post(
        `${apiDomain}/admin/orders/${orderID}/record-fill`,
        this.fillForm,
        { headers: getHeader() },
      )
        .then(() => {
          const nextStep = this.fillOrder && this.fillOrder.exType === 'SELL' ? 'Банк-перевод' : 'Broadcast'
          this.toast(
            `Settlement создан в PENDING — дальше «${nextStep}».`,
            'Fill записан', 'success',
          )
          this.closeFillModal()
          this.loadQueue()
        })
        .catch((err) => {
          const body = err.response && typeof err.response.data === 'string' ? err.response.data : ''
          let msg = body || err.message
          if (body.indexOf('exchange-order-id-required') !== -1) msg = 'Укажите Exchange Order ID.'
          else if (body.indexOf('fill-price-and-qty-required') !== -1) msg = 'Fill price и qty должны быть больше 0.'
          else if (body.indexOf('exchange-fee-invalid') !== -1) msg = 'Комиссия LP не может быть отрицательной.'
          else if (body.indexOf('reconfirm-required') !== -1) msg = 'Курс отклонился за лимит — запись fill запрещена, проведите re-confirm.'
          this.toast(msg, 'Не удалось записать fill', 'danger')
        })
        .finally(() => {
          this.fillBusy = false
          this.$set(this.actionBusy, orderID, false)
        })
    },
    openBroadcastModal(order) {
      this.broadcastOrder = order
      this.broadcastForm = this.emptyBroadcastForm()
      // Дефолт fee — индикативный network_fee_est_usd, чтоб не
      // вводить с нуля.
      this.broadcastForm.actualNetworkFeeUsd = order.networkFeeEstUsd || 0
    },
    closeBroadcastModal() {
      this.broadcastOrder = null
      this.broadcastForm = this.emptyBroadcastForm()
    },
    submitBroadcast() {
      if (!this.broadcastReady) return
      this.broadcastBusy = true
      const orderID = this.broadcastOrder.orderId
      this.$set(this.actionBusy, orderID, true)
      axios.post(
        `${apiDomain}/admin/orders/${orderID}/broadcast`,
        this.broadcastForm,
        { headers: getHeader() },
      )
        .then(() => {
          this.toast('После N подтверждений в сети нажмите Confirm.', 'Broadcast записан', 'success')
          this.closeBroadcastModal()
          this.loadQueue()
        })
        .catch((err) => {
          const body = err.response && typeof err.response.data === 'string' ? err.response.data : ''
          this.toast(body || err.message, 'Не удалось broadcast', 'danger')
        })
        .finally(() => {
          this.broadcastBusy = false
          this.$set(this.actionBusy, orderID, false)
        })
    },
    openBankTransferModal(order) {
      this.bankTransferOrder = order
      this.bankTransferForm = { bankReference: '' }
      this.bankTransferFile = null
    },
    closeBankTransferModal() {
      this.bankTransferOrder = null
      this.bankTransferForm = { bankReference: '' }
      this.bankTransferFile = null
    },
    onBankReceiptChange(event) {
      const files = event.target.files
      this.bankTransferFile = files && files.length ? files[0] : null
    },
    clearBankReceipt() {
      this.bankTransferFile = null
      if (this.$refs.bankReceiptInput) this.$refs.bankReceiptInput.value = ''
    },
    submitBankTransfer() {
      if (!this.bankTransferReady) return
      this.bankTransferBusy = true
      const orderID = this.bankTransferOrder.orderId
      this.$set(this.actionBusy, orderID, true)
      // multipart: референс перевода + обязательная платёжка.
      const form = new FormData()
      form.append('bankReference', this.bankTransferForm.bankReference)
      if (this.bankTransferFile) form.append('bankReceipt', this.bankTransferFile)
      axios.post(
        `${apiDomain}/admin/orders/${orderID}/bank-transfer`,
        form,
        { headers: { ...getHeader(), 'Content-Type': 'multipart/form-data' } },
      )
        .then(() => {
          this.toast('Перевод записан, ордер закрыт.', 'Готово', 'success')
          this.closeBankTransferModal()
          this.loadQueue()
        })
        .catch((err) => {
          const body = err.response && typeof err.response.data === 'string' ? err.response.data : ''
          let msg = body || err.message
          if (body.indexOf('receipt-required') !== -1) msg = 'Приложите платёжку — это обязательное поле.'
          else if (body.indexOf('settlement-not-ready') !== -1) msg = 'Сначала запишите fill (продажа/резервы).'
          this.toast(msg, 'Не удалось записать перевод', 'danger')
        })
        .finally(() => {
          this.bankTransferBusy = false
          this.$set(this.actionBusy, orderID, false)
        })
    },
    confirmSettlement(order) {
      if (!confirm(`Подтвердить settlement ордера #${order.orderId}? Это закроет ордер в COMPLETED.`)) return
      this.$set(this.actionBusy, order.orderId, true)
      axios.post(
        `${apiDomain}/admin/orders/${order.orderId}/confirm-settlement`,
        {},
        { headers: getHeader() },
      )
        .then(() => {
          this.toast('Settlement подтверждён. Ордер закрыт.', 'Готово', 'success')
          this.loadQueue()
        })
        .catch((err) => {
          const body = err.response && typeof err.response.data === 'string' ? err.response.data : ''
          this.toast(body || err.message, 'Не удалось confirm', 'danger')
        })
        .finally(() => {
          this.$set(this.actionBusy, order.orderId, false)
        })
    },
    hydrateUserLabel(order) {
      // Та же стратегия, что в Funding.vue: бэк возвращает только
      // userId, фронт точечно дёргает userdetails чтобы показать email.
      // Для очереди в десятки — нормально, для сотен — нужен batch.
      if (!order.userId) return
      axios.get(`${apiDomain}/exchange/userdetails/${order.userId}`, { headers: getHeader() })
        .then((res) => {
          const user = res.data || {}
          this.$set(order, 'userLabel', user.email || user.username || ('user ' + order.userId))
        })
        .catch(() => { /* fallback to 'user N' */ })
    },
    startExecution(order) {
      // Для BUY-ордеров обязателен авто KYT-check адреса получателя ДО
      // траты денег на LP. Открываем модалку, оператор жмёт «Проверить
      // адрес», реальный старт — в submitStart. Для SELL нет
      // destination_wallet, дёргаем напрямую без KYT.
      if (order.exType === 'BUY') {
        this.startTarget = order
        this.startForm = { kywNote: '' }
        this.kytResult = null
        this.kytError = ''
        this.kytReviewState = null
        this.reviewComments = { MLRO: '', COMPILANCE: '' }
        this.amlDetailsOpen = null
        // Подтягиваем последнюю KYT-проверку (если была) — чтобы сразу
        // показать вердикт/причину и блок ревью без повторного запроса.
        axios.get(`${apiDomain}/admin/orders/${order.orderId}/kyt`, { headers: getHeader() })
          .then((res) => {
            if (res.data && res.data.verdict && this.startTarget && this.startTarget.orderId === order.orderId) {
              this.kytResult = res.data
              if (res.data.verdict !== 'CLEAN') this.loadKytReview()
            }
          })
          .catch(() => { /* нет проверки — оставляем kytResult=null */ })
        return
      }
      this.doStartExecution(order, '')
    },
    closeStartModal() {
      this.startTarget = null
      this.startForm = { kywNote: '' }
      this.kytResult = null
      this.kytError = ''
      this.kytReviewState = null
      this.reviewComments = { MLRO: '', COMPILANCE: '' }
      this.amlDetailsOpen = null
    },
    toggleAmlDetails() { this.amlDetailsOpen = !this.amlDetailsVisible },
    reviewDecisionLabel(d) {
      if (d === 'ACCEPT') return '✓ Принял'
      if (d === 'REJECT') return '✕ Отклонил'
      return 'Ожидает решения'
    },
    reviewCardClass(d) {
      if (d === 'ACCEPT') return 'op-rev-accept'
      if (d === 'REJECT') return 'op-rev-reject'
      return 'op-rev-pending'
    },
    loadKytReview() {
      if (!this.startTarget) return
      axios.get(`${apiDomain}/admin/orders/${this.startTarget.orderId}/kyt-review`, { headers: getHeader() })
        .then((res) => { this.kytReviewState = res.data || null })
        .catch(() => { /* нет прав/данных — блок ревью просто не покажется */ })
    },
    // Ревьюер ставит решение под ролью role с обязательным комментарием.
    // Для ADMIN role выбирается из UI, для выделенного ревьюера бэкенд
    // всё равно перезапишет её на собственную.
    submitReview(decision, role) {
      const comment = (this.reviewComments[role] || '').trim()
      if (!this.startTarget || !comment) return
      this.reviewBusy = true
      axios.post(
        `${apiDomain}/admin/orders/${this.startTarget.orderId}/kyt-review`,
        { decision: decision, comment: comment, role: role },
        { headers: getHeader() },
      )
        .then((res) => {
          this.reviewComments[role] = ''
          // REJECT хотя бы одного ревьюера → ордер автоматически отклонён.
          if (res && res.data && res.data.rejected) {
            this.toast('Ордер отклонён по KYT-ревью, резерв возвращён клиенту.', 'Отклонено', 'warning')
            this.closeStartModal()
            this.loadQueue()
            return
          }
          // Оба приняли → исполнение стартует автоматически.
          if (res && res.data && res.data.started) {
            this.toast('Исполнение начато — оба ревьюера приняли.', 'Старт', 'success')
            this.closeStartModal()
            this.loadQueue()
            return
          }
          this.loadKytReview()
          this.toast('Решение сохранено.', 'KYT-ревью', 'success')
        })
        .catch((err) => {
          const body = err && err.response && typeof err.response.data === 'string' ? err.response.data : ''
          this.toast(body || 'Не удалось сохранить решение.', 'Ошибка', 'danger')
        })
        .finally(() => { this.reviewBusy = false })
    },
    roleLabel(role) {
      return role === 'MLRO' ? 'MLRO' : 'Compliance'
    },
    roleDecision(role) {
      if (!this.kytReviewState) return null
      return role === 'MLRO' ? this.kytReviewState.mlro : this.kytReviewState.compliance
    },
    kytVerdictLabel(verdict) {
      return ({
        CLEAN:   'CLEAN — чисто',
        FLAGGED: 'FLAGGED — риск',
        UNKNOWN: 'UNKNOWN — не определено',
      })[verdict] || verdict
    },
    // Человекочитаемое название категории риска AMLBot.
    // Тип сущности-контрагента AMLBot → человекочитаемо.
    connTypeLabel(t) {
      const key = String(t || '').toLowerCase()
      return ({
        exchange_licensed: 'Лицензир. биржа',
        exchange_unlicensed: 'Нелицензир. биржа',
        exchange: 'Биржа',
        exchange_fraudulent: 'Мошенническая биржа',
        risky_exchange: 'Рискованная биржа',
        p2p_exchange: 'P2P-биржа',
        p2p_exchange_licensed: 'P2P (лиценз.)',
        p2p_exchange_unlicensed: 'P2P (нелиценз.)',
        mixer: 'Миксер',
        sanctions: 'Санкции',
        terrorism_financing: 'Финанс. терроризма',
        dark_market: 'Даркнет-маркет',
        dark_service: 'Даркнет-сервис',
        scam: 'Скам',
        ransom: 'Вымогательство',
        stolen_coins: 'Краденые средства',
        seized_assets: 'Изъятые активы',
        illegal_service: 'Нелегальный сервис',
        enforcement_action: 'Правоохран. меры',
        malware: 'Вредоносное ПО',
        gambling: 'Гэмблинг',
        payment: 'Платёжный сервис',
        wallet: 'Кошелёк',
        atm: 'Крипто-ATM',
        miner: 'Майнинг',
        marketplace: 'Маркетплейс',
        merchant_services: 'Мерчант-сервис',
      })[key] || t
    },
    signalLabel(category) {
      return ({
        sanctions: 'Санкции',
        terrorism_financing: 'Финансирование терроризма',
        stolen_coins: 'Краденые средства',
        dark_market: 'Даркнет-маркет',
        dark_service: 'Даркнет-сервис',
        scam: 'Скам / мошенничество',
        ransom: 'Вымогательство',
        mixer: 'Миксер',
        child_exploitation: 'Эксплуатация детей',
        illegal_service: 'Нелегальный сервис',
        exchange_fraudulent: 'Мошенническая биржа',
        enforcement_action: 'Правоохранительные меры',
        seized_assets: 'Изъятые активы',
        malware: 'Вредоносное ПО',
        risky_exchange: 'Рискованная биржа',
        p2p_exchange_mlrisk_high: 'P2P — высокий риск',
        exchange: 'Биржа',
        p2p_exchange: 'P2P-биржа',
        gambling: 'Гэмблинг',
        atm: 'Крипто-ATM',
        miner: 'Майнинг',
        marketplace: 'Маркетплейс',
        payment: 'Платёжный сервис',
        wallet: 'Кошелёк',
        merchant_services: 'Мерчант-сервис',
        liquidity_pools: 'Пулы ликвидности',
        unnamed_service: 'Неназванный сервис',
        infrastructure_as_a_service: 'Инфраструктура (IaaS)',
        decentralized_exchange_contract: 'DEX-контракт',
        other: 'Прочее',
      })[category] || category
    },
    // Авто-проверка адреса получателя через AMLBot. Результат оседает
    // в p_kyt_check на бэке; вердикт определяет доступность Start.
    runKytCheck() {
      const order = this.startTarget
      if (!order) return
      this.kytBusy = true
      this.kytError = ''
      axios.post(
        `${apiDomain}/admin/orders/${order.orderId}/kyt-check`,
        {},
        { headers: getHeader() },
      )
        .then((res) => {
          this.kytResult = res.data || null
          this.kytReviewState = null
          this.amlDetailsOpen = null
          if (this.kytResult && this.kytResult.verdict !== 'CLEAN') {
            this.loadKytReview()
            this.toast(
              `Адрес: ${this.kytVerdictLabel(this.kytResult.verdict)}. Нужно двойное ревью MLRO + Compliance.`,
              'KYT проверка', 'warning',
            )
          }
        })
        .catch((err) => {
          const body = err.response && typeof err.response.data === 'string'
            ? err.response.data : ''
          this.kytError = body || 'Не удалось проверить адрес. Попробуйте ещё раз.'
        })
        .finally(() => { this.kytBusy = false })
    },
    // Шаблон причины, зависящий от введённого курса. Пустая строка,
    // если курс/цель ещё не заданы — тогда оператор введёт сам.
    buildReconfirmNote(rate) {
      const order = this.reconfirmTarget
      if (!order) return ''
      const value = Number(rate)
      if (!value || value <= 0) return ''
      const locked = Number(order.lockedPrice) || 0
      const drift = locked ? ((value - locked) / locked) * 100 : 0
      const sign = drift > 0 ? '+' : ''
      return `Актуальный курс ${value} USD за 1 ${this.cryptoTicker(order)} ` +
        `отклонился на ${sign}${drift.toFixed(2)}% от locked ${this.formatRate(locked)} — ` +
        `запрос подтверждения нового курса у клиента.`
    },
    // Оператор начал редактировать причину — перестаём авто-обновлять.
    onReconfirmNoteInput() {
      this.reconfirmNoteAuto = false
    },
    openReconfirmModal(order) {
      this.reconfirmTarget = order
      this.reconfirmNoteAuto = true
      this.reconfirmForm = { note: '', rate: null }
    },
    // Переход из fill-модалки в reconfirm, когда fill price ушёл за
    // порог. Закрываем fill, открываем reconfirm — курс предзаполняем
    // введённым fill price (можно изменить), причину генерируем из него.
    reconfirmFromFill() {
      const order = this.fillOrder
      if (!order) return
      const fillRate = Number(this.fillForm.fillPrice) || null
      this.closeFillModal()
      this.reconfirmTarget = order
      this.reconfirmNoteAuto = true
      this.reconfirmForm = { note: this.buildReconfirmNote(fillRate), rate: fillRate }
    },
    closeReconfirmModal() {
      this.reconfirmTarget = null
      this.reconfirmNoteAuto = true
      this.reconfirmForm = { note: '', rate: null }
    },
    submitReconfirm() {
      if (!this.reconfirmReady) return
      const order = this.reconfirmTarget
      const note = this.reconfirmForm.note.trim()
      const rate = Number(this.reconfirmForm.rate)
      this.reconfirmBusy = true
      this.$set(this.actionBusy, order.orderId, true)
      axios.post(
        `${apiDomain}/admin/orders/${order.orderId}/request-reconfirm`,
        { note: note, rate: rate },
        { headers: getHeader() },
      )
        .then((res) => {
          const data = res.data || {}
          const drift = (data.driftBps || 0) / 100
          const sign = drift > 0 ? '+' : ''
          this.toast(
            `Курс ${data.midPriceAtStart} отправлен клиенту. Отклонение ${sign}${drift.toFixed(2)}%. Ждём 5 мин.`,
            'Reconfirm запрошен', 'info',
          )
          this.closeReconfirmModal()
          this.loadQueue()
        })
        .catch((err) => {
          const status = err.response && err.response.status
          const body = err.response && typeof err.response.data === 'string' ? err.response.data : ''
          let msg = 'Не удалось отправить на reconfirm.'
          if (status === 409 && body.indexOf('order-not-executing') !== -1) {
            msg = 'Ордер не в EXECUTING или fill уже записан — обновите очередь.'
          } else if (body.indexOf('reconfirm-rate-required') !== -1) {
            msg = 'Введите актуальный курс больше 0.'
          } else if (status === 503) {
            msg = 'Биржа курсов недоступна, попробуйте позже.'
          }
          this.toast(msg, 'Ошибка', 'danger')
        })
        .finally(() => {
          this.reconfirmBusy = false
          this.$set(this.actionBusy, order.orderId, false)
        })
    },
    submitStart() {
      if (!this.canStart || !this.startTarget) return
      const order = this.startTarget
      this.startBusy = true
      this.doStartExecution(order, this.startForm.kywNote.trim())
        .finally(() => {
          this.startBusy = false
          this.closeStartModal()
        })
    },
    doStartExecution(order, kywNote) {
      this.$set(this.actionBusy, order.orderId, true)
      return axios.post(
        `${apiDomain}/admin/orders/${order.orderId}/start-execution`,
        { kywNote: kywNote },
        { headers: getHeader() },
      )
        .then((res) => {
          const data = res.data || {}
          if (data.newStatus === 'AWAITING_RECONFIRM') {
            const drift = (data.driftBps || 0) / 100
            const sign = drift > 0 ? '+' : ''
            this.toast(
              `Цена ушла на ${sign}${drift.toFixed(2)}% — заявка отправлена клиенту на подтверждение.`,
              'Reconfirm запрошен', 'warning',
            )
          } else {
            this.toast('Клиенту виден статус EXECUTING.', 'Исполнение начато', 'success')
          }
          this.loadQueue()
        })
        .catch((err) => {
          const status = err.response && err.response.status
          const body = err.response && typeof err.response.data === 'string' ? err.response.data : ''
          let msg = 'Не удалось начать исполнение.'
          if (status === 409) msg = 'Статус ордера уже изменился — обновите очередь.'
          if (body.indexOf('kyt-required') !== -1) {
            msg = 'Сначала проверьте адрес получателя (KYT).'
          } else if (body.indexOf('kyt-review-pending') !== -1) {
            msg = 'Вердикт не CLEAN — нужны решения обоих ревьюеров (MLRO + Compliance).'
            this.loadKytReview()
          } else if (body.indexOf('kyt-review-rejected') !== -1) {
            msg = 'Один из ревьюеров отклонил — исполнение заблокировано, отмените ордер.'
            this.loadKytReview()
          } else if (status === 503 && body.indexOf('rate-source') !== -1) {
            msg = 'Биржа курсов недоступна, попробуйте позже.'
          }
          this.toast(msg, 'Ошибка', 'danger')
        })
        .finally(() => {
          this.$set(this.actionBusy, order.orderId, false)
        })
    },
    openHistory(order) {
      this.historyOrder = order
      this.historyEvents = []
      this.historyLoading = true
      axios.get(`${apiDomain}/admin/orders/${order.orderId}/events`, { headers: getHeader() })
        .then((res) => { this.historyEvents = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.historyEvents = [] })
        .finally(() => { this.historyLoading = false })
    },
    closeHistory() {
      this.historyOrder = null
      this.historyEvents = []
    },
    historyIcon(event) {
      return ({
        CREATED:              '🆕',
        KYW_CHECKED:          '🛡',
        START_EXECUTION:      '⚡',
        AWAITING_RECONFIRM:   '🔄',
        RECONFIRM_ACCEPTED:   '✓',
        RECONFIRM_REJECTED:   '✕',
        RECONFIRM_EXPIRED:    '⌛',
        CANCELLED_BY_CLIENT:  '✕',
        CANCELLED_BY_ADMIN:   '⛔',
        EXPIRED_BY_TTL:       '⌛',
        LP_FILLED:                '🏷',
        SETTLEMENT_BROADCAST:     '📤',
        SETTLEMENT_BANK_TRANSFER: '🏦',
        SETTLEMENT_CONFIRMED:     '🔗',
        COMPLETED:                '🏁',
      })[event.eventType] || '•'
    },
    historyDotClass(event) {
      return ({
        CREATED:              'oh-dot-info',
        KYW_CHECKED:          'oh-dot-ok',
        START_EXECUTION:      'oh-dot-info',
        AWAITING_RECONFIRM:   'oh-dot-warn',
        RECONFIRM_ACCEPTED:   'oh-dot-ok',
        RECONFIRM_REJECTED:   'oh-dot-no',
        RECONFIRM_EXPIRED:    'oh-dot-muted',
        CANCELLED_BY_CLIENT:  'oh-dot-muted',
        CANCELLED_BY_ADMIN:   'oh-dot-no',
        EXPIRED_BY_TTL:       'oh-dot-muted',
        LP_FILLED:                'oh-dot-info',
        SETTLEMENT_BROADCAST:     'oh-dot-info',
        SETTLEMENT_BANK_TRANSFER: 'oh-dot-info',
        SETTLEMENT_CONFIRMED:     'oh-dot-ok',
        COMPLETED:                'oh-dot-ok',
      })[event.eventType] || ''
    },
    historyLabel(event) {
      return ({
        CREATED:              'Заявка создана',
        KYW_CHECKED:          'Адрес проверен (KYW)',
        START_EXECUTION:      'Трейдер начал исполнение',
        AWAITING_RECONFIRM:   'Запрошено ре-подтверждение клиента',
        RECONFIRM_ACCEPTED:   'Клиент принял новый курс',
        RECONFIRM_REJECTED:   'Клиент отказался от нового курса',
        RECONFIRM_EXPIRED:    'TTL ре-подтверждения истёк',
        CANCELLED_BY_CLIENT:  'Отменено клиентом',
        CANCELLED_BY_ADMIN:   'Отменено оператором',
        EXPIRED_BY_TTL:       'Истекло по TTL',
        LP_FILLED:                'Записан fill на партнёрской бирже',
        SETTLEMENT_BROADCAST:     'Транзакция отправлена в сеть',
        SETTLEMENT_BANK_TRANSFER: 'Банковский перевод USD клиенту',
        SETTLEMENT_CONFIRMED:     'Подтверждена в блокчейне',
        COMPLETED:                'Ордер закрыт',
      })[event.eventType] || event.eventType
    },
    toggleRawPayload(eventId) {
      this.$set(this.rawPayloadOpen, eventId, !this.rawPayloadOpen[eventId])
    },
    // payloadRows — типизированный рендер payload'а под тип события.
    // Возвращает [{label, value, kind?, cssClass?, oldValue?, newValue?}]
    // — простой набор строк, который шаблон превращает в визуальную
    // таблицу. Для неизвестных типов событий возвращаем fallback
    // (key:value pairs с гуманизированными ключами).
    payloadRows(event) {
      const p = event.payload || {}
      if (!Object.keys(p).length) return []

      const cryptoTicker = event.payload && event.payload.buyAsset && event.payload.buyAsset !== 'USD'
        ? event.payload.buyAsset
        : (event.payload && event.payload.sellAsset && event.payload.sellAsset !== 'USD'
            ? event.payload.sellAsset
            : '')

      switch (event.eventType) {
        case 'CREATED':
          return this.compact([
            p.lockedPrice && this.row('Курс', `1 ${cryptoTicker || 'unit'} = ${this.formatRate(p.lockedPrice)} USD`),
            p.sellAmount && p.sellAsset && this.row('Списано', this.formatAmount(p.sellAmount, p.sellAsset)),
            p.buyAmount && p.buyAsset && this.row('К получению', this.formatAmount(p.buyAmount, p.buyAsset)),
            p.companyFeeUsd != null && this.row('Комиссия Payton',
              p.buyAsset === 'USD'
                ? this.formatAmount(p.companyFeeCrypto != null ? p.companyFeeCrypto : (p.lockedPrice ? p.companyFeeUsd / p.lockedPrice : 0), p.sellAsset)
                : this.formatAmount(p.companyFeeUsd, 'USD')),
            p.expiresAt && this.row('Действителен до', this.formatDateTimeFull(p.expiresAt)),
          ])

        case 'START_EXECUTION':
          return this.compact([
            p.midAtStart && this.row('Свежий mid', `${this.formatRate(p.midAtStart)} USD`),
            p.driftBps != null && this.row('Дрифт', this.formatDrift(p.driftBps), 'op-history-drift'),
          ])

        case 'AWAITING_RECONFIRM':
          return this.compact([
            p.reconfirmRate && this.row('Новая цена', `${this.formatRate(p.reconfirmRate)} USD`),
            p.driftBps != null && this.row('Отклонение', this.formatDrift(p.driftBps), 'op-history-drift'),
            p.reconfirmExpiresAt && this.row('Срок ответа клиента', this.formatDateTimeFull(p.reconfirmExpiresAt)),
          ])

        case 'RECONFIRM_ACCEPTED':
          return this.compact([
            (p.prevLockedPrice && p.newLockedPrice) && this.ratePair('Курс',
              `${this.formatRate(p.prevLockedPrice)} USD`,
              `${this.formatRate(p.newLockedPrice)} USD`),
            (p.prevBuyAmount && p.newBuyAmount) && this.ratePair('К получению',
              this.formatAmountBare(p.prevBuyAmount),
              this.formatAmountBare(p.newBuyAmount)),
            (p.prevSellAmount && p.newSellAmount) && this.ratePair('Списание',
              this.formatAmountBare(p.prevSellAmount),
              this.formatAmountBare(p.newSellAmount)),
            p.newCompanyFeeUsd != null && this.row('Новая комиссия', this.formatAmount(p.newCompanyFeeUsd, 'USD')),
          ])

        case 'EXPIRED_BY_TTL':
          return this.compact([
            p.releasedAmount && this.row('Возвращено в баланс',
              this.formatAmount(p.releasedAmount, p.sellAsset || '')),
          ])

        case 'LP_FILLED':
          return this.compact([
            this.row('Источник', p.fillSource === 'INTERNAL' ? 'Собственные резервы' : 'Биржа (LP)'),
            p.exchangeOrderId && this.row(p.fillSource === 'INTERNAL' ? 'Референс' : 'Exchange order ID', p.exchangeOrderId, 'op-history-mono'),
            p.fillPrice != null && this.row('Реальная цена', `${this.formatRate(p.fillPrice)} USD`),
            // Реализованный спред: насколько цена закупки ушла от курса,
            // зафиксированного клиенту (locked_price). locked берём из ордера.
            (p.fillPrice != null && this.historyOrder && Number(this.historyOrder.lockedPrice) > 0) &&
              this.row('Спред к курсу клиента',
                this.formatDrift((p.fillPrice - this.historyOrder.lockedPrice) / this.historyOrder.lockedPrice * 10000),
                'op-history-drift'),
            p.fillQty != null && this.row('Купили / продали', this.formatAmountBare(p.fillQty)),
            p.exchangeFeeUsd != null && this.row(p.fillSource === 'INTERNAL' ? 'Комиссия' : 'Комиссия LP', this.formatAmount(p.exchangeFeeUsd, 'USD')),
          ])

        case 'SETTLEMENT_BROADCAST':
          return this.compact([
            p.txHash && this.row('tx_hash', p.txHash, 'op-history-mono'),
            p.actualNetworkFeeUsd != null && this.row('Реальный network fee', this.formatAmount(p.actualNetworkFeeUsd, 'USD')),
          ])

        case 'SETTLEMENT_BANK_TRANSFER':
          return this.compact([
            p.bankReference && this.row('Референс перевода', p.bankReference, 'op-history-mono'),
          ])

        case 'COMPLETED':
          return this.compact([
            p.settleKind && this.row('Тип расчёта', this.settleKindLabel(p.settleKind)),
            p.creditAmount != null && p.creditAsset && this.row('Зачислено клиенту', this.formatAmount(p.creditAmount, p.creditAsset)),
          ])

        // Для всех остальных типов событий — generic fallback с
        // человеко-читаемыми ключами.
        default:
          return Object.entries(p).map(([k, v]) => this.row(this.humanizeKey(k), this.humanizeValue(v)))
      }
    },
    downloadBankReceipt(orderId) {
      // Файл под JWT — тянем blob и открываем в новой вкладке.
      axios.get(`${apiDomain}/admin/orders/${orderId}/bank-receipt`, {
        headers: getHeader(),
        responseType: 'blob',
      })
        .then((res) => {
          const url = window.URL.createObjectURL(res.data)
          window.open(url, '_blank')
          setTimeout(() => window.URL.revokeObjectURL(url), 60000)
        })
        .catch(() => {
          this.toast('Не удалось загрузить платёжку.', 'Ошибка', 'danger')
        })
    },
    settleKindLabel(kind) {
      return ({
        INTERNAL: 'Внутренний (USD на баланс)',
        ONCHAIN:  'On-chain (отправка крипты)',
        BANK:     'Банковский перевод USD',
      })[kind] || kind
    },
    row(label, value, cssClass) {
      return { label, value, cssClass }
    },
    ratePair(label, oldValue, newValue) {
      return { label, kind: 'rate-pair', oldValue, newValue }
    },
    compact(rows) {
      return rows.filter(Boolean)
    },
    formatDrift(bps) {
      const pct = bps / 100
      const sign = pct > 0 ? '+' : ''
      return sign + pct.toFixed(2) + '%'
    },
    formatAmountBare(value) {
      // Сумма без обозначения актива — для случаев типа было→стало
      // где label уже несёт контекст.
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 8 }).format(Number(value) || 0)
    },
    humanizeKey(key) {
      // camelCase → «Слова с пробелами». Простой fallback.
      return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())
    },
    humanizeValue(value) {
      if (value == null) return '—'
      if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')
      return String(value)
    },
    formatDateTimeFull(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString('ru-RU', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      })
    },
    canCancel(order) {
      const status = order.effectiveStatus || order.status
      if (status === 'PENDING' || status === 'AWAITING_RECONFIRM') return true
      if (status === 'EXECUTING') {
        // Нет settlement (fill ещё не записан) — резерв locked, отмена
        // = release. Есть settlement PENDING (fill записан, broadcast
        // нет) — отмена = refund. В обоих случаях кнопка нужна.
        // BROADCAST/CONFIRMED — крипта в сети, отмена невозможна.
        if (!order.settlement) return true
        return order.settlement.status === 'PENDING'
      }
      return false
    },
    emptyCancelForm() {
      return { reason: '', comment: '' }
    },
    cancelOrder(order) {
      // Открываем модалку. submitCancel → реальный API-вызов.
      this.cancelTarget = order
      this.cancelForm = this.emptyCancelForm()
    },
    closeCancelModal() {
      this.cancelTarget = null
      this.cancelForm = this.emptyCancelForm()
    },
    submitCancel() {
      if (!this.cancelReady || !this.cancelTarget) return
      const orderID = this.cancelTarget.orderId
      // Сериализуем note как «REASON: comment» — бэк хранит как
      // плоский текст в p_order_event.note. UI «История» отрисует это
      // как одно поле; машинная аналитика разделит по двоеточию.
      const reason = this.cancelForm.reason
      const comment = this.cancelForm.comment.trim()
      const note = comment ? `${reason}: ${comment}` : reason

      this.cancelBusy = true
      this.$set(this.actionBusy, orderID, true)
      axios.post(
        `${apiDomain}/admin/orders/${orderID}/cancel`,
        { note: note },
        { headers: getHeader() },
      )
        .then(() => {
          this.closeCancelModal()
          this.loadQueue()
        })
        .catch((err) => {
          const body = err.response && typeof err.response.data === 'string' ? err.response.data : ''
          this.toast(body || err.message, 'Не удалось отменить', 'danger')
        })
        .finally(() => {
          this.cancelBusy = false
          this.$set(this.actionBusy, orderID, false)
        })
    },
    expireStale() {
      if (!confirm('Перевести все истекшие PENDING-ордера в EXPIRED и вернуть резервы клиентам?')) return
      this.expireBusy = true
      axios.post(`${apiDomain}/admin/orders/expire-stale`, {}, { headers: getHeader() })
        .then((res) => {
          const n = (res.data && res.data.expired) || 0
          this.toast(`Истекших ордеров переведено: ${n}.`, 'Завершено', 'success')
          this.loadQueue()
        })
        .catch((err) => {
          console.error('expire-stale failed:', err)
          this.toast('Не удалось прогнать TTL.', 'Ошибка', 'danger')
        })
        .finally(() => { this.expireBusy = false })
    },
    remainingMs(order) {
      const expires = Date.parse(order.expiresAt || '')
      if (!Number.isFinite(expires)) return 0
      return Math.max(0, expires - this.now)
    },
    // Остаток времени у клиента на ответ по ре-подтверждению курса
    // (AWAITING_RECONFIRM). Считается от reconfirm_expires_at, который
    // бэкенд кладёт в ордер. 0 — если срок истёк или поля нет.
    reconfirmRemainingMs(order) {
      const expires = Date.parse(order.reconfirmExpiresAt || '')
      if (!Number.isFinite(expires)) return 0
      return Math.max(0, expires - this.now)
    },
    rowClass(order) {
      const ms = this.remainingMs(order)
      if (ms === 0 && order.status === 'PENDING') return 'op-row-expired'
      if (ms < 5 * 60 * 1000 && order.status === 'PENDING') return 'op-row-warn'
      return ''
    },
    formatAmount(value, asset) {
      const fractionDigits = asset === 'USD' ? 2 : 8
      const n = Number(value || 0)
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: asset === 'USD' ? 2 : 0,
        maximumFractionDigits: fractionDigits,
      }).format(n) + ' ' + (asset || '')
    },
    // Комиссия Payton: для SELL — в крипто-активе (нативное поле
    // companyFeeCrypto; деление на курс — фолбэк для старых ордеров),
    // для BUY — в USD.
    formatFee(order) {
      if (order.exType === 'SELL') {
        let crypto
        if (order.companyFeeCrypto != null) {
          crypto = Number(order.companyFeeCrypto)
        } else {
          const rate = Number(order.lockedPrice) || 0
          crypto = rate ? (Number(order.companyFeeUsd) || 0) / rate : 0
        }
        return this.formatAmount(crypto, order.sellAsset)
      }
      return this.formatAmount(order.companyFeeUsd, 'USD')
    },
    formatRate(value) {
      const n = Number(value || 0)
      const digits = n >= 100 ? 2 : n >= 1 ? 4 : 6
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: digits }).format(n)
    },
    formatRemaining(ms) {
      const totalSeconds = Math.floor(ms / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      const pad = (n) => (n < 10 ? '0' + n : String(n))
      return pad(minutes) + ':' + pad(seconds)
    },
    statusLabel(status) {
      switch ((status || '').toUpperCase()) {
        case 'PENDING':            return 'Ожидает'
        case 'AWAITING_RECONFIRM': return 'Жд. подтв.'
        case 'EXECUTING':          return 'Исполняется'
        case 'COMPLETED':          return 'Завершён'
        case 'EXPIRED':            return 'Истёк'
        case 'CANCELLED':          return 'Отменён клиентом'
        case 'REJECTED':           return 'Отклонён бэкофисом'
        default:                   return status || '—'
      }
    },
    statusCss(status) {
      switch ((status || '').toUpperCase()) {
        case 'PENDING':            return 'op-status-pending'
        case 'AWAITING_RECONFIRM': return 'op-status-warn'
        case 'EXECUTING':          return 'op-status-info'
        case 'COMPLETED':          return 'op-status-ok'
        case 'EXPIRED':            return 'op-status-muted'
        case 'CANCELLED':          return 'op-status-muted'
        case 'REJECTED':           return 'op-status-no'
        default:                   return ''
      }
    },
  },
}
</script>

<style scoped>
.orders-page {
  padding: 24px;
  max-width: 1300px;
  margin: 0 auto;
  color: #111;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 13px;
}
.op-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 20px; }
.op-title { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; }
.op-sub { font-size: 12px; color: #6B7280; margin-top: 4px; }
.op-stats { display: flex; gap: 10px; }
.op-stat { background: #fff; border: 1px solid #EAECF0; border-radius: 10px; padding: 10px 14px; min-width: 150px; }
.op-stat-lbl { font-size: 9.5px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; letter-spacing: .06em; }
.op-stat-val { font-size: 16px; font-weight: 800; color: #0A0A0A; margin-top: 4px; }

.op-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 14px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #EAECF0;
  border-radius: 10px;
}
.op-filter-group { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.op-filter-label {
  font: 700 10px/1 Inter;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #9CA3AF;
  margin-right: 4px;
}
.op-chip {
  border: 1px solid #EAECF0;
  background: #fff;
  padding: 6px 12px;
  border-radius: 999px;
  font: 600 11px/1 Inter;
  color: #374151;
  cursor: pointer;
  transition: all .12s;
}
.op-chip:hover { background: #F9FAFB; }
.op-chip-active {
  background: #0D7A5F;
  border-color: #0D7A5F;
  color: #fff;
}
.op-chip-active:hover { background: #0B6A52; }

.op-card { background: #fff; border: 1px solid #EAECF0; border-radius: 12px; overflow: hidden; }
.op-card-head {
  padding: 12px 18px; background: #FAFAFA; border-bottom: 1px solid #F0F2F5;
  display: flex; align-items: center; justify-content: space-between;
  font-size: 12px; font-weight: 700; color: #0A0A0A;
}

.op-btn-ghost, .op-btn-no {
  border: 1px solid #EAECF0; background: #fff;
  padding: 6px 12px; border-radius: 7px;
  font: 600 11px/1 Inter; cursor: pointer; transition: all .12s;
}
.op-btn-ghost { color: #374151; }
.op-btn-ghost:hover { background: #F9FAFB; }
.op-btn-no { background: #FEF2F2; border-color: #FECACA; color: #991B1B; }
.op-btn-no:hover { background: #FEE2E2; }
.op-btn-ghost:disabled, .op-btn-no:disabled { opacity: 0.55; cursor: not-allowed; }

.op-table-wrap {
  overflow-x: auto;
  /* Тонкая scrollbar — только когда таблица шире контейнера. */
  scrollbar-width: thin;
  scrollbar-color: #C7CBD2 transparent;
}
.op-table-wrap::-webkit-scrollbar { height: 8px; }
.op-table-wrap::-webkit-scrollbar-thumb { background: #C7CBD2; border-radius: 4px; }
.op-table-wrap::-webkit-scrollbar-track { background: transparent; }

.op-table {
  width: 100%;
  min-width: 1180px; /* 60 + 9 колонок × ~120px — держим верстку */
  border-collapse: collapse;
}
.op-table thead th {
  padding: 8px 14px; text-align: left; font-size: 9.5px; font-weight: 700;
  color: #9CA3AF; text-transform: uppercase; letter-spacing: .06em;
  border-bottom: 1px solid #F0F2F5; background: #FAFAFA; white-space: nowrap;
}
.op-table tbody td { padding: 11px 14px; border-bottom: 1px solid #F5F6F8; font-size: 12.5px; color: #374151; white-space: nowrap; }
.op-table tbody tr:last-child td { border-bottom: none; }
.op-table tbody tr.op-row-warn { background: #FFFBEB; }
.op-table tbody tr.op-row-expired { background: #FEF2F2; }

.op-extype {
  font: 700 11px/1 Inter; padding: 3px 8px; border-radius: 5px;
}
.op-extype-buy { background: #ECFDF5; color: #065F46; }
.op-extype-sell { background: #EFF6FF; color: #1D4ED8; }

.op-amount { font-weight: 700; color: #0A0A0A; }
.op-net {
  display: inline-block; margin-left: 4px; padding: 1px 6px;
  background: #F3F4F6; color: #6B7280;
  font-size: 9.5px; font-weight: 700; border-radius: 4px; letter-spacing: 0.02em;
}
.op-rate { font-family: ui-monospace, SFMono-Regular, monospace; color: #6B7280; }
.op-fee { color: #6B7280; }

.op-status { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 5px; }
.op-status-pending { background: #FFFBEB; color: #92400E; }
.op-status-warn    { background: #FEF2F2; color: #991B1B; }
.op-status-info    { background: #EFF6FF; color: #1D4ED8; }
.op-status-ok      { background: #ECFDF5; color: #065F46; }
.op-status-muted   { background: #F3F4F6; color: #6B7280; }
.op-status-no      { background: #FEF2F2; color: #991B1B; }

.op-when { font-family: ui-monospace, SFMono-Regular, monospace; color: #6B7280; font-size: 12px; }
.op-when-urgent { color: #DC2626; font-weight: 700; }
.op-empty { text-align: center; padding: 32px 14px; color: #9CA3AF; font-style: italic; }

.op-row-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}
.op-row-actions .op-btn-ok,
.op-row-actions .op-btn-no {
  padding: 5px 10px;
  font-size: 11px;
  border-radius: 6px;
  border: 1px solid;
  cursor: pointer;
  background: #fff;
  font-weight: 700;
  transition: all .12s;
}
.op-row-actions .op-btn-ok { color: #065F46; border-color: #6EE7B7; background: #ECFDF5; }
.op-row-actions .op-btn-ok:hover { background: #D1FAE5; }
.op-row-actions .op-btn-no { color: #991B1B; border-color: #FECACA; background: #FEF2F2; }
.op-row-actions .op-btn-no:hover { background: #FEE2E2; }
.op-row-actions .op-btn-ok:disabled,
.op-row-actions .op-btn-no:disabled { opacity: .55; cursor: not-allowed; }
.op-pending-label {
  font-size: 10.5px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 5px;
  background: #FEF3C7;
  color: #92400E;
}
.op-pending-label.op-pending-info { background: #EFF6FF; color: #1D4ED8; }

/* AWAITING_RECONFIRM: ждём ответа клиента по новому курсу — лейбл,
   предложенный курс и обратный отсчёт до авто-отмены. */
.op-reconfirm-wait {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}
.op-reconfirm-rate {
  font-size: 10px;
  font-weight: 600;
  color: #92400E;
}
.op-reconfirm-left {
  font-size: 10px;
  font-weight: 700;
  color: #B45309;
  font-variant-numeric: tabular-nums;
}
.op-reconfirm-left.op-reconfirm-expired { color: #DC2626; }

.op-btn-link {
  background: transparent;
  border: none;
  color: #0D7A5F;
  font: 600 11px/1 Inter;
  cursor: pointer;
  padding: 5px 8px;
  text-decoration: underline;
}
.op-btn-link:hover { color: #16A37F; }

.op-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(15,23,42,0.45);
  z-index: 1000;
  display: flex; align-items: center; justify-content: center;
}
.op-modal {
  background: #fff;
  border-radius: 14px;
  width: 640px; max-width: 92vw; max-height: 85vh;
  display: flex; flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0,0,0,0.25);
}
.op-modal-head {
  padding: 14px 18px;
  border-bottom: 1px solid #F0F2F5;
  font: 700 14px/1 Inter;
  display: flex; justify-content: space-between; align-items: center;
}
.op-modal-close { background: none; border: 0; font-size: 22px; line-height: 1; color: #9CA3AF; cursor: pointer; }
.op-modal-body { padding: 16px 18px; overflow-y: auto; flex: 1; }

.op-history-list { display: flex; flex-direction: column; gap: 14px; }
.op-history-item { display: flex; gap: 12px; }
.op-history-dot {
  width: 30px; height: 30px; flex-shrink: 0;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px;
  border: 1px solid #E5E7EB;
  background: #F9FAFB;
}
.oh-dot-info  { border-color: #93C5FD; background: #EFF6FF; }
.oh-dot-ok    { border-color: #6EE7B7; background: #ECFDF5; }
.oh-dot-warn  { border-color: #FCD34D; background: #FFFBEB; }
.oh-dot-no    { border-color: #FCA5A5; background: #FEF2F2; }
.oh-dot-muted { border-color: #D1D5DB; background: #F9FAFB; }

.op-history-body { flex: 1; min-width: 0; }
.op-history-row { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.op-history-label { font: 700 12.5px/1.3 Inter; color: #0A0A0A; }
.op-history-time { font-size: 10.5px; color: #9CA3AF; font-variant-numeric: tabular-nums; }
.op-history-actor { font-size: 11px; color: #6B7280; margin-top: 2px; }
.op-history-note {
  margin-top: 4px;
  font: italic 500 11.5px/1.4 Inter;
  color: #92400E;
  background: #FFFBEB;
  border-left: 3px solid #FCD34D;
  padding: 4px 9px;
  border-radius: 4px;
}
.op-history-rows {
  margin: 8px 0 4px;
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 14px;
  row-gap: 4px;
}
.op-history-pair-k {
  font: 600 11px/1.4 Inter;
  color: #6B7280;
}
.op-history-pair-v {
  font: 600 11.5px/1.4 Inter;
  color: #0A0A0A;
  word-break: break-word;
}
.op-history-mono {
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 10.5px;
  color: #374151;
}
.op-history-drift { color: #B45309; }
.op-rate-old { color: #9CA3AF; text-decoration: line-through; }
.op-rate-arrow { margin: 0 6px; color: #9CA3AF; }
.op-rate-new { color: #0A0A0A; font-weight: 700; }

.op-btn-toggle {
  background: transparent;
  border: 0;
  padding: 4px 0;
  margin-top: 6px;
  font: 600 10px/1 Inter;
  color: #9CA3AF;
  cursor: pointer;
  letter-spacing: .04em;
  text-transform: uppercase;
}
.op-btn-toggle:hover { color: #16A37F; }

.op-history-payload {
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 10.5px;
  color: #374151;
  background: #F9FAFB;
  border: 1px solid #EAECF0;
  border-radius: 6px;
  padding: 8px 10px;
  margin: 6px 0 0;
  white-space: pre-wrap;
}

.op-row-actions .op-btn-info {
  padding: 5px 10px;
  font-size: 11px;
  border-radius: 6px;
  border: 1px solid #93C5FD;
  background: #EFF6FF;
  color: #1D4ED8;
  cursor: pointer;
  font-weight: 700;
}
.op-row-actions .op-btn-info:hover { background: #DBEAFE; }
.op-row-actions .op-btn-info:disabled { opacity: .55; cursor: not-allowed; }

.op-form-row { margin-bottom: 12px; }
.op-form-row label {
  display: block;
  font-size: 10.5px;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 5px;
}
.op-form-row input,
.op-form-row select,
.op-form-row textarea {
  width: 100%;
  padding: 8px 11px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font: 500 13px/1.3 Inter;
  outline: none;
  background: #fff;
  color: #0A0A0A;
  font-family: inherit;
}
.op-form-row textarea { resize: vertical; min-height: 60px; line-height: 1.5; }
.op-form-row input:focus,
.op-form-row select:focus,
.op-form-row textarea:focus { border-color: #16A37F; box-shadow: 0 0 0 3px rgba(22,163,127,0.12); }
.op-form-row input.op-input-invalid { border-color: #FCA5A5; background: #FEF2F2; }
.op-form-row input.op-input-invalid:focus { border-color: #EF4444; box-shadow: 0 0 0 3px rgba(239,68,68,0.12); }

.op-source-toggle { display: flex; gap: 8px; }
.op-source-opt {
  flex: 1; padding: 8px 12px; border: 1px solid #D1D5DB; border-radius: 8px;
  background: #fff; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer;
}
.op-source-opt:hover { border-color: #9CA3AF; }
.op-source-opt.active { border-color: #0D7A5F; background: #ECFDF5; color: #0D7A5F; }

.op-bank-card {
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
}
.op-bank-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
}
.op-bank-row + .op-bank-row { border-top: 1px solid #EEF0F3; }
.op-bank-key { font-size: 11px; font-weight: 700; color: #6B7280; text-transform: uppercase; letter-spacing: .04em; }
.op-bank-val { font-size: 13px; font-weight: 600; color: #0A0A0A; text-align: right; }

.op-form-block {
  border: 1px solid #EEF0F3;
  border-radius: 10px;
  padding: 12px 12px 4px;
  margin-bottom: 12px;
  background: #FBFBFC;
}
.op-form-block-title {
  font-size: 11px;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 10px;
}

.op-file-picked {
  margin-top: 8px;
  font-size: 12px;
  color: #16A37F;
  display: flex;
  align-items: center;
  gap: 8px;
}
.op-file-clear {
  border: none;
  background: none;
  color: #EF4444;
  font-size: 11px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

.op-cancel-warn {
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #991B1B;
  border-radius: 8px;
  padding: 10px 13px;
  margin-bottom: 14px;
  font-size: 11.5px;
  line-height: 1.5;
}
.op-cancel-warn b { color: #7F1D1D; }
.op-form-hint code {
  background: #F3F4F6;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
}
.op-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.op-form-sub {
  margin-top: 4px;
  font-size: 10.5px;
  line-height: 1.4;
  color: #6B7280;
}
.op-form-sub b { color: #B45309; }

.op-fill-promise {
  background: #F0FDF4;
  border: 1px solid #BBF7D0;
  border-radius: 8px;
  padding: 10px 13px;
  margin-bottom: 14px;
}
.op-fill-promise-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  line-height: 1.6;
}
.op-fill-promise-row span { color: #166534; }
.op-fill-promise-row b { color: #14532D; font-weight: 700; }
.op-addr-mono {
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 11.5px;
  word-break: break-all;
  text-align: right;
  max-width: 60%;
}
.op-fill-deviation {
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 8px;
  padding: 11px 13px;
  margin: 14px 0;
}
.op-fill-deviation-head { font-size: 12.5px; color: #92400E; font-weight: 600; }
.op-fill-deviation-head b { color: #B45309; }
.op-fill-deviation-body { font-size: 11.5px; color: #78350F; line-height: 1.5; margin-top: 4px; }
.op-fill-deviation-cta { margin-top: 10px; width: 100%; }
.op-fill-deviation-gain {
  background: #F0FDF4;
  border-color: #BBF7D0;
}
.op-fill-deviation-gain .op-fill-deviation-head { color: #166534; }
.op-fill-deviation-gain .op-fill-deviation-head b { color: #15803D; }
.op-fill-deviation-gain .op-fill-deviation-body { color: #14532D; }
/* Нейтральный инфо-блок: спред в пределах лимита. */
.op-fill-deviation-info {
  background: #F8FAFC;
  border-color: #E2E8F0;
}
.op-fill-deviation-info .op-fill-deviation-head { color: #334155; }
.op-fill-deviation-info .op-fill-deviation-head b { color: #0F172A; }
.op-fill-deviation-info .op-fill-deviation-body { color: #475569; }
.op-kyt {
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #FAFAFA;
  border: 1px solid #F0F2F5;
  border-radius: 8px;
}
.op-kyt-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.op-kyt-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
}
.op-kyt-clean   { background: #ECFDF5; color: #047857; border-color: #A7F3D0; }
.op-kyt-flagged { background: #FEF2F2; color: #B91C1C; border-color: #FECACA; }
.op-kyt-unknown { background: #FFFBEB; color: #92400E; border-color: #FDE68A; }
.op-kyt-error {
  margin-top: 8px;
  font-size: 12px;
  color: #B91C1C;
}
.op-kyt-link {
  display: inline-block;
  margin-top: 8px;
  color: #0D7A5F;
  font-weight: 700;
  font-size: 12px;
}
.op-kyt-link:hover { text-decoration: underline; }
.op-kyt-reason {
  margin-top: 8px;
  padding: 8px 10px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 7px;
}
.op-kyt-msg { font-size: 12px; color: #92400E; }
.op-kyt-reason-title { margin-top: 6px; font-size: 11.5px; font-weight: 700; color: #6B7280; }
.op-kyt-sig-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.op-kyt-sig {
  font-size: 11.5px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #F3F4F6;
  color: #374151;
  border: 1px solid #E5E7EB;
}
.op-kyt-sig-risky { background: #FEF2F2; color: #B91C1C; border-color: #FECACA; font-weight: 700; }
.op-kyt-flagreason { background: #FEF2F2; border-color: #FECACA; }
.op-kyt-flagreason .op-kyt-msg { color: #B91C1C; }
.op-kyt-details { margin-top: 8px; padding: 8px 10px; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 7px; }
.op-kyt-dev { font-size: 11.5px; color: #92400E; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 6px; padding: 4px 8px; margin-bottom: 6px; }
.op-kyt-drow { font-size: 12px; color: #374151; margin: 3px 0; }
.op-kyt-drow > span:first-child { color: #6B7280; }
.op-kyt-conn { display: flex; flex-wrap: wrap; gap: 8px; align-items: baseline; font-size: 11.5px; padding: 4px 0; border-bottom: 1px solid #F0F2F5; }
.op-kyt-conn:last-child { border-bottom: none; }
.op-kyt-conn-name { font-weight: 700; color: #111827; }
.op-kyt-conn-type { color: #6B7280; }
.op-kyt-conn-risk { color: #374151; }
.op-kyt-conn-meta { color: #9CA3AF; margin-left: auto; }
.op-kyt-conn-risky .op-kyt-conn-name,
.op-kyt-conn-risky .op-kyt-conn-risk { color: #B91C1C; }

.op-kyt-toggle {
  display: inline-flex; align-items: center; gap: 6px; margin-top: 6px; padding: 4px 8px;
  background: #F3F4F6; border: 1px solid #E5E7EB; border-radius: 6px; cursor: pointer;
  font-size: 11.5px; font-weight: 700; color: #6B7280;
}
.op-kyt-toggle:hover { background: #E5E7EB; }
.op-kyt-toggle-chev { font-size: 10px; }
.op-kyt-review { margin: 10px 0; padding: 10px; border: 1px solid #E5E7EB; border-radius: 8px; background: #FBFBFC; }
.op-kyt-note { font-size: 12px; color: #6B7280; margin-top: 6px; }
.op-form-lbl { display: block; font-size: 12px; font-weight: 600; color: #374151; margin: 8px 0 4px; }
.op-kyt-rev-grid { display: flex; gap: 8px; margin-top: 6px; }
.op-kyt-rev-card { flex: 1; padding: 8px 10px; border: 1px solid #E5E7EB; border-radius: 7px; background: #fff; }
.op-kyt-rev-role { font-size: 11px; font-weight: 700; color: #6B7280; text-transform: uppercase; }
.op-kyt-rev-dec { font-size: 13px; font-weight: 700; margin-top: 2px; color: #374151; }
.op-rev-accept { border-color: #A7F3D0; background: #ECFDF5; }
.op-rev-accept .op-kyt-rev-dec { color: #047857; }
.op-rev-reject { border-color: #FECACA; background: #FEF2F2; }
.op-rev-reject .op-kyt-rev-dec { color: #B91C1C; }
.op-rev-pending .op-kyt-rev-dec { color: #9CA3AF; }
.op-kyt-rev-comment { font-size: 12px; color: #4B5563; margin-top: 6px; font-style: italic; }
.op-kyt-rev-actions { display: flex; gap: 8px; margin-top: 6px; }
.op-kyt-rev-actions button { padding: 6px 14px; border-radius: 7px; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.op-kyt-rev-action { margin-top: 10px; padding-top: 10px; border-top: 1px dashed #E5E7EB; }
.op-kyt-rev-action-head { font-size: 12px; font-weight: 700; color: #374151; margin-bottom: 5px; }
.op-kyt-rev-action-head span { font-weight: 400; color: #6B7280; }
.op-kyt-review textarea {
  width: 100%; box-sizing: border-box; padding: 8px 10px; border: 1px solid #D1D5DB;
  border-radius: 7px; font-size: 13px; font-family: inherit; resize: vertical; min-height: 56px; line-height: 1.5;
}
.op-kyt-review textarea:focus { border-color: #16A37F; box-shadow: 0 0 0 3px rgba(22,163,127,0.12); outline: none; }

.op-form-hint {
  font-size: 11.5px;
  color: #6B7280;
  background: #FAFAFA;
  border: 1px solid #F0F2F5;
  border-radius: 7px;
  padding: 9px 11px;
  line-height: 1.5;
  margin-top: 6px;
}
.op-btn-ghost {
  border: 1px solid #EAECF0;
  background: #fff;
  padding: 7px 12px;
  border-radius: 7px;
  font: 600 12px/1 Inter;
  cursor: pointer;
  color: #374151;
}
.op-btn-ghost:hover { background: #F9FAFB; }
.op-modal-foot {
  padding: 12px 18px;
  background: #FAFAFA;
  border-top: 1px solid #F0F2F5;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.op-modal-foot .op-btn-ok { padding: 7px 14px; font-size: 12px; }
</style>
